import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Pin, RefreshCw, AlertTriangle, Info, Lightbulb, Code, Edit, Trash2, GripVertical, Move, Image, ChevronUp, ChevronDown, Save, Languages } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import QCMComponent from './QCMComponent';
import RichTextEditor from './RichTextEditor';
import GalleryImageProposalsEditor from './GalleryImageProposalsEditor';
import RecursiveSubsectionChildren from './RecursiveSubsectionChildren';
import HierarchyCreateActions from './HierarchyCreateActions';
import ContextualHierarchyCreatePanel from './ContextualHierarchyCreatePanel';
import ContentTable from './ContentTable';
import SafeRichContent from './SafeRichContent';
import TableEditor from './TableEditor';
import VisualTableListEditor from './VisualTableListEditor';
import { getAnchoredTableIds, removeTableAnchor } from './bookTableAnchor';
import { resolveBookMediaUrl as buildImageUrl } from './bookMediaUrl';
import {
  getGalleryImages,
  normalizeImageSlots,
  normalizeTableShape,
} from './contentLayout';
import ImageLightbox from '../ImageLightbox';
import api from '../../services/api';
import { translateWithLanguage } from '../../i18n/i18n';
import {
  findSubsection,
  flattenSubsections,
} from '../../utils/contentTree';
import {
  captureViewportAnchor,
  restoreViewportAnchorAfterRender,
} from './viewportAnchor';

const FullBookContent = ({
  bookData,
  selectedItem,
  navigationRequestId = 0,
  isAdmin,
  canEditStructure = false,
  onRegenerateChapter,
  regenLoading,
  onBookContentChanged,
  contentLanguage = 'fr',
  selectedLang = 'source',
}) => {
  const sourceLanguage =
    bookData?.book?.language || bookData?.language || 'fr';
  const canManageImageStructure =
    canEditStructure &&
    (selectedLang === 'source' || selectedLang === sourceLanguage);
  const [displayTitle, setDisplayTitle] = useState(bookData?.title || bookData?.book?.title || '');
  const [editMode, setEditMode] = useState(false);
  const [pendingTitle, setPendingTitle] = useState(displayTitle);
  const bookId = useMemo(() => (bookData?.book?.id || bookData?.id || null), [bookData]);
  const progressTimerRef = useRef(null);
  const saveCheckTimerRef = useRef(null);
  const lastScrollTsRef = useRef(0);
  const anchorRef = useRef({ chapter_id: null, section_id: null, subsection_id: null });
  const anchorChangedTsRef = useRef(0);
  const pendingPayloadRef = useRef(null);
  const lastSavedPayloadRef = useRef(null); // Dernier payload sauvegardé pour éviter les requêtes répétées
  const handledNavigationRequestRef = useRef(0);
  const [progressPct, setProgressPct] = useState(0);
  const [editStates, setEditStates] = useState({
    chapters: {},
    sections: {},
    subsections: {},
  });
  const [tableValidity, setTableValidity] = useState({});

  const [createStates, setCreateStates] = useState({
    chapter: { open: false, title: '', content: '', images: [], tables: [], position: 'first', afterId: '' },
  });
  const [createChapterTableValidity, setCreateChapterTableValidity] = useState({});
  const [activeCreateTarget, setActiveCreateTarget] = useState(null);

  const [dragState, setDragState] = useState({ type: null, id: null });
  const [dragDropMode, setDragDropMode] = useState(false); // Mode drag & drop activé/désactivé
  const [draggedItem, setDraggedItem] = useState(null); // { type: 'chapter'|'section'|'subsection', id, chapterId?, sectionId? }
  const [uploadingCover, setUploadingCover] = useState(false); // État d'upload de la couverture
  const [detectLanguageLoading, setDetectLanguageLoading] = useState(false);
  const coverInputRef = useRef(null); // Référence pour l'input file de la couverture
  const [qcmLanguages, setQcmLanguages] = useState({}); // Langue sélectionnée pour chaque chapitre { chapterId: 'fr'|'en'|'pt' }
  const [localBookData, setLocalBookData] = useState(null); // Copie locale pour les modifications
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Indicateur de modifications non sauvegardées
  const [lightbox, setLightbox] = useState({ open: false, src: '', alt: '', caption: '' });

  const CHARS_PER_MIN = 1000;
  const MIN_READ_MS = 5000;
  const MAX_READ_MS = 180000;
  const READ_RATIO = 0.8;

  const textLen = (s) => (typeof s === 'string' ? s.length : 0);
  const getAnchorLen = (type, id) => {
    let len = 0;
    const chapters = Array.isArray(bookData?.chapters) ? bookData.chapters : [];
    for (const ch of chapters) {
      if (type === 'chapter' && ch?.id === id) {
        const override = editStates.chapters[ch.id]?.contentOverride;
        len = textLen(override ?? ch?.content);
        break;
      }
      if (type === 'section' && Array.isArray(ch?.sections)) {
        for (const sec of ch.sections) {
          if (sec?.id === id) {
            const override = editStates.sections[sec.id]?.contentOverride;
            len = textLen(override ?? sec?.content);
            break;
          }
        }
        if (len) break;
      }
      if (type === 'subsection' && Array.isArray(ch?.sections)) {
        for (const sec of ch.sections) {
          const found = findSubsection(sec?.subsections || [], id);
          if (found) {
            const override =
              editStates.subsections[found.node.id]?.contentOverride;
            len = textLen(override ?? found.node?.content);
            break;
          }
        }
        if (len) break;
      }
    }
    return len;
  };

  const renderRichOrLegacyContent = (content, images = [], tables = []) => {
    if (!content || typeof content !== 'string') return null;
    return (
      <SafeRichContent
        content={content}
        images={images}
        contentLanguage={contentLanguage}
        tables={tables}
        className="space-y-2"
        onImageClick={(image) => {
          const src = buildImageUrl(image?.url || '');
          if (!src) return;
          const caption = image?.caption || 'Image';
          setLightbox({
            open: true,
            src,
            alt: caption,
            caption,
          });
        }}
      />
    );
  };
  const estimateReadMs = (type, id) => {
    const chars = getAnchorLen(type, id);
    const cps = CHARS_PER_MIN / 60;
    let ms = Math.ceil((chars / cps) * 1000);
    if (ms < MIN_READ_MS) ms = MIN_READ_MS;
    if (ms > MAX_READ_MS) ms = MAX_READ_MS;
    return ms;
  };

  // Calculer le pourcentage basé sur le numéro du chapitre actuel
  const calculateRealPercentage = (chapterId, sectionId = null, subsectionId = null) => {
    if (!bookData?.chapters || !chapterId) return 0;
    
    const totalChapters = bookData.chapters.length;
    if (totalChapters === 0) return 0;
    
    // Trouver l'index du chapitre actuel (0-indexed)
    const currentChapterIndex = bookData.chapters.findIndex(c => c.id === chapterId);
    if (currentChapterIndex === -1) return 0;
    
    // Calculer le pourcentage : (chapitre actuel / total) * 100
    // +1 car on compte le chapitre comme "lu" quand on y est
    const percentage = Math.round(((currentChapterIndex + 1) / totalChapters) * 100);
    
    // console.log('📊 Calcul pourcentage:', {
    //   chapterId,
    //   currentChapterIndex: currentChapterIndex + 1,
    //   totalChapters,
    //   percentage
    // });
    
    return percentage;
  };

  useEffect(() => {
    setDisplayTitle(bookData?.title || bookData?.book?.title || '');
    setPendingTitle(bookData?.title || bookData?.book?.title || '');
  }, [bookData?.title, bookData?.book?.title]);

  useEffect(() => {
    if (bookData) {
      setLocalBookData(JSON.parse(JSON.stringify(bookData))); // Deep copy
      setHasUnsavedChanges(false);
      
      // Initialiser qcmLanguages avec la langue du livre pour tous les chapitres
      const bookLanguage = bookData?.book?.language || bookData?.language || 'fr';
      const initialLanguages = {};
      const chapters = bookData?.chapters || [];
      chapters.forEach(chapter => {
        if (chapter?.id && !qcmLanguages[chapter.id]) {
          initialLanguages[chapter.id] = bookLanguage;
        }
      });
      if (Object.keys(initialLanguages).length > 0) {
        setQcmLanguages(prev => ({ ...prev, ...initialLanguages }));
      }
    }
  }, [bookData]);

  const handleDetectLanguage = async () => {
    if (!bookId) return;
    try {
      setDetectLanguageLoading(true);
      const res = await api.post(`/books/${bookId}/detect-language/`);
      const langCode = res?.data?.language || '';
      let langLabel = 'Langue inconnue';
      if (langCode === 'fr') langLabel = 'Français (fr)';
      else if (langCode === 'en') langLabel = 'Anglais (en)';
      else if (langCode === 'pt') langLabel = 'Portugais (pt)';
      else if (langCode) langLabel = langCode;

      if (typeof onBookContentChanged === 'function') {
        await onBookContentChanged();
      }
      toast.success(`Langue détectée : ${langLabel}`);
    } catch (e) {
      console.error('Erreur lors de la détection de la langue', e);
      const msg = e?.response?.data?.error || e?.message || 'Erreur lors de la détection de la langue';
      toast.error(msg);
    } finally {
      setDetectLanguageLoading(false);
    }
  };

  const retranslateScope = async (scope, payload = {}) => {
    if (!bookId) return;
    const loadingToast = toast.loading('Relance de la traduction en cours...', {
      position: 'top-right',
    });
    try {
      await api.post(`/books/${bookId}/retranslate/`, {
        scope,
        ...payload,
      });
      toast.success(
        'Traduction relancée. Le contenu sera mis à jour après traitement.',
        {
          id: loadingToast,
          duration: 4000,
          position: 'top-right',
        }
      );
    } catch (e) {
      console.error('Erreur lors de la relance de la traduction', e);
      const msg =
        e?.response?.data?.error ||
        e?.message ||
        'Erreur lors de la relance de la traduction';
      toast.error(msg, {
        id: loadingToast,
        duration: 5000,
        position: 'top-right',
      });
    }
  };
  const processInlineFormatting = (text) => {
    if (!text || typeof text !== 'string') return text;

    // Regex pour détecter toutes les balises inline : <gras>, <i>, <u>, <grasi>, <grasu>, <iugras>, <grasiu>, etc. et <span> avec style
    const parts = [];
    let lastIndex = 0;
    // Regex améliorée pour toutes les combinaisons possibles y compris les spans
    const inlineRegex = /<(gras|i|u|grasi|grasu|iugras|grasiu|gruis|igras|ugrasi|ugris|uigras|span)(?:\s+[^>]*style="([^"]*)"[^>]*|[^>]*)>(.*?)<\/(gras|i|u|grasi|grasu|iugras|grasiu|gruis|igras|ugrasi|ugris|uigras|span)>/gi;
    let match;

    while ((match = inlineRegex.exec(text)) !== null) {
      // Ajouter le texte avant la balise
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
      }
      
      // Ajouter le texte formaté
      const tagType = match[1].toLowerCase();
      const style = match[2] || '';
      const content = match[3];
      
      if (tagType === 'span' && style) {
        // Gérer les spans avec style (couleur, police)
        parts.push({ type: 'span', content, style });
      } else {
        parts.push({ type: tagType, content });
      }
      
      lastIndex = inlineRegex.lastIndex;
    }

    // Ajouter le reste du texte
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.slice(lastIndex) });
    }

    // Si aucune balise trouvée, retourner le texte tel quel
    if (parts.length === 0) return text;

    // Construire les éléments React
    return parts.map((part, idx) => {
      if (part.type === 'gras') {
        return <strong key={idx}>{part.content}</strong>;
      } else if (part.type === 'i') {
        return <em key={idx}>{part.content}</em>;
      } else if (part.type === 'u') {
        return <u key={idx}>{part.content}</u>;
      } else if (part.type === 'grasi') {
        return <strong key={idx}><em>{part.content}</em></strong>;
      } else if (part.type === 'grasu') {
        return <strong key={idx}><u>{part.content}</u></strong>;
      } else if (part.type === 'iugras') {
        return <em key={idx}><u>{part.content}</u></em>;
      } else if (part.type === 'grasiu') {
        return <strong key={idx}><em><u>{part.content}</u></em></strong>;
      } else if (part.type === 'gruis') {
        return <strong key={idx}><u><em>{part.content}</em></u></strong>;
      } else if (part.type === 'igras') {
        return <em key={idx}><strong><u>{part.content}</u></strong></em>;
      } else if (part.type === 'ugrasi') {
        return <u key={idx}><strong><em>{part.content}</em></strong></u>;
      } else if (part.type === 'ugris') {
        return <u key={idx}><strong><em>{part.content}</em></strong></u>;
      } else if (part.type === 'uigras') {
        return <u key={idx}><em><strong>{part.content}</strong></em></u>;
      } else if (part.type === 'span') {
        return <span key={idx} style={{ 
          color: part.style.includes('color:') ? part.style.match(/color:\s*([^;]+)/)?.[1] : undefined,
          fontFamily: part.style.includes('font-family:') ? part.style.match(/font-family:\s*([^;]+)/)?.[1] : undefined 
        }}>{part.content}</span>;
      } else {
        return <span key={idx}>{part.content}</span>;
      }
    });
  };

  // Composant pour mettre en évidence les mots spéciaux (warning, reminder, note)
  // IMPORTANT: Détection uniquement au DÉBUT de la ligne (pas au milieu d'une phrase)
  const getInlineCalloutType = (rawText) => {
    if (!rawText || typeof rawText !== 'string') return null;

    const trimmed = rawText.trimStart();
    const detectionText = trimmed
      .replace(/^~\s*/, '')
      .replace(/^[\u2022\u2023\u25E6\u2043\u2219•\-\*]\s+/, '')
      .replace(/^\d+[\.\)]\s+/, '')
      .replace(/^[a-zA-Z][\.\)]\s+/, '')
      .trimStart();
    const detectionLower = detectionText.toLowerCase();

    if (
      /^(?:warning)\s*(?::|!|—|–|-)/.test(detectionLower) ||
      detectionLower.startsWith("n'oubliez pas")
    ) {
      return 'warning';
    }
    if (/^(?:reminder|remember|rappelle)\s*(?::|!|—|–|-)/.test(detectionLower)) {
      return 'reminder';
    }
    if (/^(?:note|remarque)\s*(?::|!|—|–|-)/.test(detectionLower)) {
      return 'note';
    }
    return null;
  };

  const HighlightedText = ({ text, disableCallouts = false }) => {
    if (!text) return null;

    if (disableCallouts) {
      return <>{processInlineFormatting(text)}</>;
    }

    const calloutType = getInlineCalloutType(text);

    // WARNING: / "N'oubliez pas" => texte rouge + icône de warning + fond rouge léger
    // Seulement si au DÉBUT de la ligne (ex: "WARNING: texte" mais pas "this is a warning about")
    if (calloutType === 'warning') {
      return (
        <div className="my-4 flex w-full items-start gap-2 rounded-md border-2 border-red-700 bg-red-600 px-4 py-3 text-lg text-white font-semibold [&_*]:!text-white [&_li]:marker:!text-white">
          <AlertTriangle size={16} className="mt-0.5 text-white flex-shrink-0" />
          <div className="flex-1">
            <p className="mb-0 leading-relaxed">{processInlineFormatting(text)}</p>
          </div>
        </div>
      );
    }

    // REMINDER: / REMEMBER: / RAPPELLE: => texte jaune/orange + fond orange clair
    // Seulement si au DÉBUT de la ligne
    if (calloutType === 'reminder') {
      return (
        <div className="my-4 flex w-full items-start gap-2 rounded-md border-2 border-amber-700 bg-amber-600 px-4 py-3 text-lg text-white font-semibold [&_*]:!text-white [&_li]:marker:!text-white">
          <Pin size={16} className="mt-0.5 text-white flex-shrink-0" />
          <div className="flex-1">
            <p className="mb-0 leading-relaxed">{processInlineFormatting(text)}</p>
          </div>
        </div>
      );
    }

    // NOTE: / REMARQUE: => texte bleu avec fond bleu ciel clair (seulement si au début de la ligne)
    // Supporte aussi les lignes qui commencent par ~ Note: ou ~ Remarque:
    if (calloutType === 'note') {
      return (
        <div className="my-4 flex w-full items-start gap-2 rounded-md border-2 border-blue-700 bg-blue-600 px-4 py-3 text-lg text-white font-semibold [&_*]:!text-white [&_li]:marker:!text-white">
          <Pin size={16} className="mt-0.5 text-white flex-shrink-0" />
          <div className="flex-1">
            <p className="mb-0 leading-relaxed">{processInlineFormatting(text)}</p>
          </div>
        </div>
      );
    }

    // Texte normal avec formatage inline
    return <>{processInlineFormatting(text)}</>;
  };

  // Nettoyer les titres qui contiennent déjà une numérotation (même logique que Sidebar)
  const cleanTitle = (title, order = null) => {
    if (!title) return '';
    if (order !== null) {
      const regex = new RegExp(`^${order}\.\s*`);
      if (regex.test(title)) {
        return title.replace(regex, '');
      }
    }
    const generalNumberingRegex = /^(\d+\.?\s*)+/;
    if (generalNumberingRegex.test(title)) {
      return title.replace(generalNumberingRegex, '');
    }
    return title;
  };

  const renderVideos = (videos, displayConfig = {}) => {
    if (!videos || videos.length === 0) return null;

    const size = displayConfig?.size || 'medium';
    const columns = displayConfig?.columns || 2;

    const sizeClasses = {
      small: 'h-32',
      medium: 'h-48',
      large: 'h-64',
    };

    const columnClasses = {
      1: 'grid-cols-1',
      2: 'grid-cols-1 md:grid-cols-2',
      3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    };

    const heightClass = sizeClasses[size] || sizeClasses.medium;
    const gridClass = columnClasses[columns] || columnClasses[2];

    return (
      <div className="w-full my-6">
        <div className={`grid ${gridClass} gap-4`}>
          {videos.map((video, index) => {
            const videoPath = typeof video === 'string' ? video : video?.url;
            const caption =
              typeof video === 'string'
                ? `Vidéo ${index + 1}`
                : (video?.caption || `Vidéo ${index + 1}`);
            const fullUrl = buildImageUrl(videoPath || '');

            return (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                {fullUrl ? (
                  <video
                    src={fullUrl}
                    controls
                    className={`w-full ${heightClass} bg-black`}
                    onError={() => {
                      console.error("Erreur de chargement de la vidéo:", fullUrl);
                    }}
                  />
                ) : (
                  <div className="p-4 text-sm text-gray-500">Entrée vidéo invalide</div>
                )}
                <p className="text-sm text-gray-600 p-2 bg-gray-50">{caption}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Fonction pour détecter si une ligne est un élément de liste
  const isBulletPoint = (text) => {
    // Détecte : • texte, - texte, 1. texte, 2) texte, a) texte, etc.
    return /^[\u2022\u2023\u25E6\u2043\u2219•\-\*]\s+/.test(text) || // Puces
           /^\d+[\.\)]\s+/.test(text) || // Numéros : 1. ou 1)
           /^[a-zA-Z][\.\)]\s+/.test(text); // Lettres : a. ou a)
  };

  const getListItemMeta = (text) => {
    if (!text) return null;

    if (/^[\u2022\u2023\u25E6\u2043\u2219•\-\*]\s+/.test(text)) {
      return { listType: 'ul', text: text.replace(/^[\u2022\u2023\u25E6\u2043\u2219•\-\*]\s+/, '') };
    }
    if (/^\d+[\.\)]\s+/.test(text)) {
      return { listType: 'ol', text: text.replace(/^\d+[\.\)]\s+/, '') };
    }
    if (/^[a-zA-Z][\.\)]\s+/.test(text)) {
      return { listType: 'ol-alpha', text: text.replace(/^[a-zA-Z][\.\)]\s+/, '') };
    }
    return null;
  };

  const renderMixedParagraphsAndLists = (rawText, opts = {}) => {
    if (!rawText || typeof rawText !== 'string') return [];

    const {
      paragraphClassName = 'mb-4 text-gray-700 leading-relaxed text-justify select-text',
      listClassName = 'mb-4 ml-6 space-y-2',
      listItemClassName = 'text-gray-700 leading-relaxed select-text',
      disableInlineCallouts = false,
    } = opts;

    let textToProcess = rawText;
    textToProcess = textToProcess.replace(/([^\n])[\s]*•[\s]*/g, '$1\n• ');

    const lines = textToProcess.split(/\n+/);
    const elements = [];

    let currentListType = null;
    let currentListItems = [];

    const flushList = (keyBase) => {
      if (!currentListItems.length || !currentListType) return;
      const Tag = (currentListType === 'ol' || currentListType === 'ol-alpha') ? 'ol' : 'ul';
      const listStyle =
        currentListType === 'ol' ? 'list-decimal' :
        currentListType === 'ol-alpha' ? 'list-[lower-alpha]' :
        'list-disc';
      elements.push(
        <Tag key={`${keyBase}-list`} className={`${listClassName} ${listStyle}`}>
          {currentListItems.map((itemText, liIdx) => (
            <li key={liIdx} className={listItemClassName}>
              <HighlightedText text={itemText} disableCallouts={disableInlineCallouts} />
            </li>
          ))}
        </Tag>
      );
      currentListType = null;
      currentListItems = [];
    };

    let listCounter = 0;
    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) return;

      if (!disableInlineCallouts && getInlineCalloutType(trimmed)) {
        flushList(`mixed-${listCounter++}`);
        elements.push(
          <HighlightedText key={`callout-${listCounter++}`} text={trimmed} />
        );
        return;
      }

      const meta = getListItemMeta(trimmed);
      if (meta) {
        if (currentListType && meta.listType !== currentListType) {
          flushList(`mixed-${listCounter++}`);
        }
        currentListType = meta.listType;
        currentListItems.push(meta.text);
        return;
      }

      flushList(`mixed-${listCounter++}`);
      elements.push(
        <p key={`p-${listCounter++}`} className={paragraphClassName}>
          <HighlightedText text={trimmed} disableCallouts={disableInlineCallouts} />
        </p>
      );
    });

    flushList(`mixed-${listCounter++}`);
    return elements;
  };

  const renderContent = (content) => {
    if (!content || typeof content !== 'string') return null;

    const blocks = [];
    // Regex pour détecter les balises spéciales : <article>, <note>, <warning>, <reminder>, <important>, <tip>, <example>, <text>, <raw>
    // <text> et <raw> permettent d'échapper du texte pour qu'il ne soit pas interprété comme une balise
    const tagRegex = /<(article|note|warning|reminder|important|tip|example|text|raw)>([\s\S]*?)<\/(article|note|warning|reminder|important|tip|example|text|raw)>/gi;
    let lastIndex = 0;
    let match;

    while ((match = tagRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        blocks.push({ type: 'text', text: content.slice(lastIndex, match.index) });
      }
      let tagType = match[1].toLowerCase(); // article, note, warning, reminder, text, raw
      // Renommer 'text' en 'rawtext' pour éviter le conflit avec le type 'text' utilisé pour le contenu normal
      if (tagType === 'text' || tagType === 'raw') {
        tagType = 'rawtext';
      }
      blocks.push({ type: tagType, text: (match[2] || '').trim() });
      lastIndex = tagRegex.lastIndex;
    }
    if (lastIndex < content.length) {
      blocks.push({ type: 'text', text: content.slice(lastIndex) });
    }
    const elements = [];

    blocks.forEach((block, blockIndex) => {
      if (block.type === 'text') {
        renderMixedParagraphsAndLists(block.text, {
          paragraphClassName: 'mb-4 text-gray-700 leading-relaxed text-justify select-text',
          listClassName: 'mb-4 ml-6 space-y-2',
          listItemClassName: 'text-gray-700 leading-relaxed select-text',
        }).forEach((el) => elements.push(React.cloneElement(el, { key: `${blockIndex}-${el.key}` })));
      } else if (block.type === 'article') {
        elements.push(
          <div
            key={`article-${blockIndex}`}
            className="my-4 border-2 border-yellow-400 bg-yellow-50 rounded-md px-4 py-3 text-base text-gray-800"
          >
            {renderMixedParagraphsAndLists(block.text, {
              paragraphClassName: 'mb-2 last:mb-0 leading-relaxed text-justify',
              listClassName: 'mb-2 ml-6 space-y-2',
              listItemClassName: 'leading-relaxed',
              disableInlineCallouts: true,
            })}
          </div>
        );
      } else if (block.type === 'note') {
        elements.push(
          <div
            key={`note-${blockIndex}`}
            className="my-4 flex w-full items-start gap-2 rounded-md border-2 border-blue-700 bg-blue-600 px-4 py-3 text-lg text-white font-semibold [&_*]:!text-white [&_li]:marker:!text-white"
          >
            <Pin size={16} className="mt-0.5 text-white flex-shrink-0" />
            <div className="flex-1">
              {renderMixedParagraphsAndLists(block.text, {
                paragraphClassName: 'mb-2 last:mb-0 leading-relaxed',
                listClassName: 'mb-2 ml-6 space-y-2',
                listItemClassName: 'leading-relaxed',
                disableInlineCallouts: true,
              })}
            </div>
          </div>
        );
      } else if (block.type === 'warning') {
        elements.push(
          <div
            key={`warning-${blockIndex}`}
            className="my-4 flex w-full items-start gap-2 rounded-md border-2 border-red-700 bg-red-600 px-4 py-3 text-lg text-white font-semibold [&_*]:!text-white [&_li]:marker:!text-white"
          >
            <AlertTriangle size={16} className="mt-0.5 text-white flex-shrink-0" />
            <div className="flex-1">
              {renderMixedParagraphsAndLists(block.text, {
                paragraphClassName: 'mb-2 last:mb-0 leading-relaxed',
                listClassName: 'mb-2 ml-6 space-y-2',
                listItemClassName: 'leading-relaxed',
                disableInlineCallouts: true,
              })}
            </div>
          </div>
        );
      } else if (block.type === 'reminder') {
        elements.push(
          <div
            key={`reminder-${blockIndex}`}
            className="my-4 flex w-full items-start gap-2 rounded-md border-2 border-amber-700 bg-amber-600 px-4 py-3 text-lg text-white font-semibold [&_*]:!text-white [&_li]:marker:!text-white"
          >
            <Pin size={16} className="mt-0.5 text-white flex-shrink-0" />
            <div className="flex-1">
              {renderMixedParagraphsAndLists(block.text, {
                paragraphClassName: 'mb-2 last:mb-0 leading-relaxed',
                listClassName: 'mb-2 ml-6 space-y-2',
                listItemClassName: 'leading-relaxed',
                disableInlineCallouts: true,
              })}
            </div>
          </div>
        );
      } else if (block.type === 'important') {
        elements.push(
          <div
            key={`important-${blockIndex}`}
            className="my-4 flex w-full items-start gap-2 rounded-md border-2 border-purple-700 bg-purple-600 px-4 py-3 text-base text-white [&_*]:!text-white [&_li]:marker:!text-white"
          >
            <Info size={16} className="mt-0.5 text-white flex-shrink-0" />
            <div className="flex-1">
              {renderMixedParagraphsAndLists(block.text, {
                paragraphClassName: 'mb-2 last:mb-0 leading-relaxed',
                listClassName: 'mb-2 ml-6 space-y-2',
                listItemClassName: 'leading-relaxed',
                disableInlineCallouts: true,
              })}
            </div>
          </div>
        );
      } else if (block.type === 'tip') {
        elements.push(
          <div
            key={`tip-${blockIndex}`}
            className="my-4 flex w-full items-start gap-2 rounded-md border-2 border-green-700 bg-green-600 px-4 py-3 text-base text-white [&_*]:!text-white [&_li]:marker:!text-white"
          >
            <Lightbulb size={16} className="mt-0.5 text-white flex-shrink-0" />
            <div className="flex-1">
              {renderMixedParagraphsAndLists(block.text, {
                paragraphClassName: 'mb-2 last:mb-0 leading-relaxed',
                listClassName: 'mb-2 ml-6 space-y-2',
                listItemClassName: 'leading-relaxed',
                disableInlineCallouts: true,
              })}
            </div>
          </div>
        );
      } else if (block.type === 'example') {
        elements.push(
          <div
            key={`example-${blockIndex}`}
            className="my-4 flex w-full items-start gap-2 rounded-md border-2 border-gray-900 bg-gray-800 px-4 py-3 text-base text-white font-mono [&_*]:!text-white [&_li]:marker:!text-white"
          >
            <Code size={16} className="mt-0.5 text-white flex-shrink-0" />
            <div className="flex-1">
              {renderMixedParagraphsAndLists(block.text, {
                paragraphClassName: 'mb-2 last:mb-0 leading-relaxed',
                listClassName: 'mb-2 ml-6 space-y-2',
                listItemClassName: 'leading-relaxed',
                disableInlineCallouts: true,
              })}
            </div>
          </div>
        );
      } else if (block.type === 'rawtext') {
        // Balise <text> ou <raw> : afficher le texte tel quel SANS aucun formatage ni détection de mots-clés
        // Utile pour échapper du texte qui contient des mots comme "warning" sans les styliser
        const lines = block.text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
        lines.forEach((line, idx) => {
          elements.push(
            <p
              key={`raw-${blockIndex}-${idx}`}
              className="mb-4 text-gray-700 leading-relaxed text-justify select-text"
            >
              {line}
            </p>
          );
        });
      }
    });

    return elements;
  };

  const renderImages = (images, displayConfig = {}) => {
    const galleryImages = getGalleryImages(images, contentLanguage);
    if (galleryImages.length === 0) return null;

    // Configuration par défaut
    const size = displayConfig?.size || 'medium'; // 'small', 'medium', 'large'
    const columns = displayConfig?.columns || 2; // 1, 2, 3

    // Classes CSS pour la taille des images
    const sizeClasses = {
      small: 'h-32',
      medium: 'h-48',
      large: 'h-64'
    };

    // Classes CSS pour le nombre de colonnes
    const columnClasses = {
      1: 'grid-cols-1',
      2: 'grid-cols-1 md:grid-cols-2',
      3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
    };

    const heightClass = sizeClasses[size] || sizeClasses.medium;
    const gridClass = columnClasses[columns] || columnClasses[2];

    return (
      <div className="w-full my-6">
        <div className={`grid ${gridClass} gap-4`}>
          {galleryImages.map((image, index) => {
            const imagePath = typeof image === 'string' ? image : image.url;
            const imageCaption = typeof image === 'string' ? `Image ${index + 1}` : (image.caption || `Illustration ${index + 1}`);
            const fullImageUrl = buildImageUrl(imagePath || '');

            return (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <img 
                  src={fullImageUrl} 
                  alt={imageCaption} 
                  className={`w-full ${heightClass} object-contain`}
                  style={{ cursor: fullImageUrl ? 'zoom-in' : 'default' }}
                  onClick={() => {
                    if (!fullImageUrl) return;
                    setLightbox({ open: true, src: fullImageUrl, alt: imageCaption, caption: imageCaption });
                  }}
                  onError={(e) => {
                    console.error("Erreur de chargement de l'image:", fullImageUrl);
                    e.target.style.display = 'none';
                    const errorDiv = document.createElement('div');
                    errorDiv.className = 'p-4 bg-red-50 border border-red-200 rounded text-red-600 text-sm';
                    errorDiv.textContent = `Image non trouvée: ${imageCaption}`;
                    e.target.parentNode.appendChild(errorDiv);
                  }}
                />
                <p className="text-sm text-gray-600 p-2 bg-gray-50">{imageCaption}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTables = (tables, content = '') => {
    if (!tables || tables.length === 0) return null;
    const anchoredIds = getAnchoredTableIds(content, tables);
    const unanchoredTables = tables.filter(
      (table) => !anchoredIds.has(table?.id)
    );
    if (unanchoredTables.length === 0) return null;
    
    return (
      <div className="w-full my-6">
        {unanchoredTables.map((table, index) => (
          <div key={table?.id || index} className="border border-gray-200 rounded-lg overflow-hidden mb-4">
            {(() => {
              // Afficher une image si le tableau est un snapshot
              const rawUrl = table?.url || table?.filepath || '';
              const url = buildImageUrl(rawUrl);
              const isSnapshot = table?.is_snapshot === true || /\.(png|jpe?g|gif|webp)$/i.test(rawUrl);
              if (isSnapshot && url) {
                return (
                  <>
                    <div className="bg-white p-4 flex justify-center items-center">
                      <img
                        src={url}
                        alt={table.title || table.caption || `Tableau ${index + 1}`}
                        className="max-w-full h-auto"
                        style={{ cursor: 'zoom-in' }}
                        onClick={() => {
                          if (!url) return;
                          const caption = table.caption || table.title || `Tableau ${index + 1}`;
                          setLightbox({ open: true, src: url, alt: caption, caption });
                        }}
                      />
                    </div>
                    <p className="text-sm text-gray-600 p-2 bg-gray-100 font-medium">{table.caption || table.title || `Tableau ${index + 1}`}</p>
                  </>
                );
              }

              return <ContentTable table={table} className="bg-white p-4" />;
            })()}
          </div>
        ))}
      </div>
    );
  };

  const parseJsonArray = (value) => {
    try {
      const parsed = JSON.parse(value || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const uploadInlineImage = async (file) => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/uploads/images/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response?.data;
  };

  const contentRef = useRef(null);

  const refreshAtAnchor = async (anchorIds) => {
    const snapshot = captureViewportAnchor(anchorIds);
    if (typeof onBookContentChanged === 'function') {
      await onBookContentChanged();
    }
    await restoreViewportAnchorAfterRender(snapshot);
  };

  const openContextualCreation = (target) => {
    if (!canEditStructure || !target || createStates.chapter.open) return;
    setActiveCreateTarget((current) => current || target);
  };

  const cancelContextualCreation = () => setActiveCreateTarget(null);

  const submitContextualCreation = async (target, draft) => {
    if (!bookId || !canEditStructure || !target) {
      throw new Error('La création hiérarchique est indisponible.');
    }

    const endpoint =
      target.entityType === 'chapter'
        ? `/books/${bookId}/chapters/`
        : target.entityType === 'section'
          ? `/books/${bookId}/chapters/${target.chapterId}/sections/`
          : `/books/${bookId}/chapters/${target.chapterId}/sections/${target.sectionId}/subsections/`;
    const payload = {
      title: draft.title.trim(),
      content: draft.content || '',
      images: draft.images || [],
      tables: draft.tables || [],
      position: target.position,
    };

    if (target.afterId !== null) payload.after_id = target.afterId;
    if (target.entityType === 'subsection') payload.parent_id = target.parentId;

    await api.post(endpoint, payload);
    try {
      await refreshAtAnchor(
        [
          target.sourceAnchorId,
          target.sectionId ? `section-${target.sectionId}` : null,
          target.chapterId ? `chapter-${target.chapterId}` : null,
        ].filter(Boolean)
      );
    } catch (refreshError) {
      console.error(
        'Élément créé, mais le rafraîchissement du livre a échoué',
        refreshError
      );
      toast.error(
        "L'élément a bien été créé, mais le livre n'a pas pu être rafraîchi. Rechargez la page pour le voir.",
        { duration: 5000, position: 'top-right' }
      );
    } finally {
      setActiveCreateTarget(null);
    }
  };

  const renderContextualCreation = (context, label) => {
    if (!canEditStructure) return null;
    const sourceKey = `${context.currentType}:${context.currentItem.id}`;

    return (
      <div className="mt-3 grid min-w-0 gap-2">
        <HierarchyCreateActions
          label={label}
          context={context}
          activeActionKey={activeCreateTarget?.actionKey || ''}
          creationLocked={
            Boolean(activeCreateTarget) || createStates.chapter.open
          }
          onOpen={openContextualCreation}
        />
        {activeCreateTarget?.sourceKey === sourceKey && (
          <ContextualHierarchyCreatePanel
            key={activeCreateTarget.actionKey}
            target={activeCreateTarget}
            onUploadImage={uploadInlineImage}
            onSubmit={submitContextualCreation}
            onCancel={cancelContextualCreation}
          />
        )}
      </div>
    );
  };

  // Un changement de données ou de sélection ne doit jamais déplacer la lecture.
  // Seule une demande explicite provenant du sommaire déclenche ce défilement.
  useEffect(() => {
    if (
      !navigationRequestId ||
      handledNavigationRequestRef.current === navigationRequestId
    ) {
      return;
    }
    handledNavigationRequestRef.current = navigationRequestId;

    if (selectedItem && contentRef.current && bookData && bookData.chapters) {
      console.log('🎯 Défilement vers:', selectedItem);
      
      let elementId = '';
      
      if (selectedItem.type === 'chapter' && selectedItem.chapterIndex !== undefined) {
        const chapter = bookData.chapters[selectedItem.chapterIndex];
        if (chapter && chapter.id) {
          elementId = `chapter-${chapter.id}`;
          console.log('📍 ID chapitre:', elementId, 'Chapitre:', chapter.title);
        }
      } else if (selectedItem.type === 'section' && selectedItem.chapterIndex !== undefined && selectedItem.sectionIndex !== undefined) {
        const chapter = bookData.chapters[selectedItem.chapterIndex];
        if (chapter && chapter.sections && chapter.sections[selectedItem.sectionIndex]) {
          const section = chapter.sections[selectedItem.sectionIndex];
          if (section && section.id) {
            elementId = `section-${section.id}`;
            console.log('📍 ID section:', elementId, 'Section:', section.title);
            console.log('📍 Chapter index:', selectedItem.chapterIndex, 'Section index:', selectedItem.sectionIndex);
          }
        }
      } else if (selectedItem.type === 'subsection' && selectedItem.chapterIndex !== undefined && selectedItem.sectionIndex !== undefined && selectedItem.subsectionId !== undefined) {
        const chapter = bookData.chapters[selectedItem.chapterIndex];
        if (chapter && chapter.sections && chapter.sections[selectedItem.sectionIndex]) {
          const section = chapter.sections[selectedItem.sectionIndex];
          const found = findSubsection(
            section.subsections || [],
            selectedItem.subsectionId
          );
          if (found?.node?.id) {
            elementId = `subsection-${found.node.id}`;
            console.log('📍 ID sous-section récursive:', elementId, 'Sous-section:', found.node.title);
          }
        }
      } else if (selectedItem.type === 'subsection' && selectedItem.chapterIndex !== undefined && selectedItem.sectionIndex !== undefined && selectedItem.subsectionIndex !== undefined) {
        const chapter = bookData.chapters[selectedItem.chapterIndex];
        if (chapter && chapter.sections && chapter.sections[selectedItem.sectionIndex]) {
          const section = chapter.sections[selectedItem.sectionIndex];
          if (section && section.subsections && section.subsections[selectedItem.subsectionIndex]) {
            const subsection = section.subsections[selectedItem.subsectionIndex];
            if (subsection && subsection.id) {
              elementId = `subsection-${subsection.id}`;
              console.log('📍 ID sous-section:', elementId, 'Sous-section:', subsection.title);
            }
          }
        }
      } else if (selectedItem.type === 'qcm' && selectedItem.chapterIndex !== undefined && selectedItem.qcmIndex !== undefined) {
        const chapter = bookData.chapters[selectedItem.chapterIndex];
        if (chapter && chapter.id) {
          elementId = `chapter-${chapter.id}-qcm-${selectedItem.qcmIndex}`;
          console.log('📍 ID QCM:', elementId, 'Chapitre:', chapter.title, 'QCM Index:', selectedItem.qcmIndex);
        }
      }
      
      if (elementId) {
        console.log('🔍 Recherche de l\'élément avec ID:', elementId);
        const element = document.getElementById(elementId);
        if (element) {
          console.log('✅ Élément trouvé:', element.tagName, element.textContent);
          // Attendre un peu que le contenu soit rendu
          setTimeout(() => {
            // Calculer précisément la hauteur du header fixe
            const mainHeader = document.querySelector('.sticky.top-0');
            const bookTitleHeader = document.querySelector('.sticky.top-0.bg-white.z-10');
            
            let totalHeaderHeight = 0;
            
            // Header principal (navigation)
            if (mainHeader) {
              totalHeaderHeight += mainHeader.offsetHeight;
              // console.log('📐 Header principal:', mainHeader.offsetHeight);
            }
            
            // Header du titre du livre (s'il existe)
            if (bookTitleHeader && bookTitleHeader !== mainHeader) {
              totalHeaderHeight += bookTitleHeader.offsetHeight;
              // console.log('📐 Header titre livre:', bookTitleHeader.offsetHeight);
            }
            
            // console.log('📐 Hauteur totale des headers:', totalHeaderHeight);
            
            // Calculer la position de l'élément dans le conteneur de défilement
            // console.log('🔍 Début de la recherche du conteneur de défilement');
            
            // D'abord chercher le conteneur le plus proche
            let scrollContainer = element.closest('.overflow-y-auto');
            // console.log('🔍 closest(.overflow-y-auto):', !!scrollContainer);
            
            // Si aucun conteneur trouvé, chercher dans les parents plus largement
            if (!scrollContainer) {
              // console.log('🔍 Recherche dans les parents...');
              let parent = element.parentElement;
              let level = 0;
              while (parent && !scrollContainer && level < 10) {
                console.log(`🔍 Parent niveau ${level}:`, parent.tagName, parent.className);
                if (parent.classList.contains('overflow-y-auto')) {
                  scrollContainer = parent;
                  console.log('✅ Conteneur trouvé dans les parents!');
                }
                parent = parent.parentElement;
                level++;
              }
            }
            
            // Si toujours pas trouvé, chercher globalement
            if (!scrollContainer) {
              console.log('🔍 Recherche globale...');
              scrollContainer = document.querySelector('.overflow-y-auto');
              // console.log('🔍 querySelector(.overflow-y-auto):', !!scrollContainer);
            }
            
            // Alternative: chercher par d'autres sélecteurs courants
            if (!scrollContainer) {
              // console.log('🔍 Recherche avec sélecteurs alternatifs...');
              const selectors = [
                '.overflow-y-auto',
                '[class*="overflow-y"]',
                '.overflow-auto',
                '[class*="overflow"]'
              ];
              
              for (const selector of selectors) {
                const container = document.querySelector(selector);
                if (container) {
                  console.log(`✅ Conteneur trouvé avec selector: ${selector}`);
                  scrollContainer = container;
                  break;
                }
              }
            }
            
            console.log('🔍 Scroll container trouvé:', !!scrollContainer);
            
            if (scrollContainer) {
              console.log('📐 Scroll container:', scrollContainer.tagName, scrollContainer.className);
              
              // S'assurer que le conteneur peut défiler
              if (scrollContainer.scrollHeight > scrollContainer.clientHeight) {
                console.log('✅ Conteneur peut défiler');
                
                // Position absolue de l'élément dans le conteneur
                const containerRect = scrollContainer.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();
                const scrollTop = scrollContainer.scrollTop;
                
                console.log('📐 Container rect:', { top: containerRect.top, left: containerRect.left, width: containerRect.width, height: containerRect.height });
                console.log('📐 Element rect:', { top: elementRect.top, left: elementRect.left, width: elementRect.width, height: elementRect.height });
                console.log('📐 Current scroll top:', scrollTop);
                
                // Position de l'élément par rapport au début du contenu
                const elementOffsetTop = elementRect.top - containerRect.top + scrollTop;
                
                // Position finale avec compensation du header et marge supplémentaire
                const marginOffset = 80; // Marge confortable sous le header
                const finalScrollPosition = Math.max(0, elementOffsetTop - totalHeaderHeight - marginOffset);
                
                console.log('📐 Element offset top:', elementOffsetTop);
                console.log('📐 Total header height:', totalHeaderHeight);
                console.log('📐 Margin offset:', marginOffset);
                console.log('📐 Final scroll position:', finalScrollPosition);
                
                // Défiler vers la position calculée
                scrollContainer.scrollTo({
                  top: finalScrollPosition,
                  behavior: 'smooth'
                });
                
                // Vérifier que le défilement a bien eu lieu
                setTimeout(() => {
                  console.log('📐 Scroll position après défilement:', scrollContainer.scrollTop);
                }, 300);
              } else {
                console.log('⚠️ Conteneur ne peut pas défiler, utilisation de window.scrollTo()');
                
                // Utiliser window.scrollTo() comme fallback
                const elementRect = element.getBoundingClientRect();
                const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
                
                console.log('📐 Element rect (window):', { top: elementRect.top, left: elementRect.left });
                console.log('📐 Current window scroll:', currentScroll);
                
                // Calculer la position absolue de l'élément par rapport à la page
                const elementPageTop = elementRect.top + currentScroll;
                
                // Position finale avec compensation du header
                const marginOffset = 80;
                const finalWindowScrollPosition = Math.max(0, elementPageTop - totalHeaderHeight - marginOffset);
                
                console.log('📐 Element page top:', elementPageTop);
                console.log('📐 Final window scroll position:', finalWindowScrollPosition);
                
                // Défiler la fenêtre entière
                window.scrollTo({
                  top: finalWindowScrollPosition,
                  behavior: 'smooth'
                });
                
                // Vérifier que le défilement a bien eu lieu
                setTimeout(() => {
                  console.log('📐 Window scroll position après défilement:', window.pageYOffset || document.documentElement.scrollTop);
                }, 300);
              }
            } else {
              console.log('❌ Aucun conteneur de défilement trouvé');
              // Lister tous les éléments avec overflow-y-auto pour le débogage
              const allScrollContainers = document.querySelectorAll('.overflow-y-auto');
              console.log('📋 Tous les conteneurs de défilement trouvés:', allScrollContainers.length);
              allScrollContainers.forEach((container, index) => {
                console.log(`📋 Container ${index}:`, container.tagName, container.className);
              });
              
              // Utiliser scrollIntoView comme dernier recours
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest'
              });
            }
          }, 150);
        } else {
          console.log('❌ Élément non trouvé avec ID:', elementId);
          // Lister tous les IDs disponibles pour le débogage
          const allElements = document.querySelectorAll('[id^="chapter-"], [id^="section-"], [id^="subsection-"]');
          console.log('📋 IDs disponibles:', Array.from(allElements).map(el => el.id));
        }
      }
    }
  }, [navigationRequestId, selectedItem, bookData]);

  useEffect(() => {
    if (!bookId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/books/${bookId}/reading-progress/`);
        if (cancelled) return;
        const data = res?.data || {};
        
        // Recalculer le pourcentage basé sur le chapitre actuel
        let calculatedPercentage = 0;
        if (data.chapter_id && bookData?.chapters) {
          const totalChapters = bookData.chapters.length;
          const currentChapterIndex = bookData.chapters.findIndex(c => c.id === data.chapter_id);
          if (currentChapterIndex !== -1 && totalChapters > 0) {
            calculatedPercentage = Math.round(((currentChapterIndex + 1) / totalChapters) * 100);
          }
        }
        // Garder le pourcentage le plus élevé entre celui de la BDD et le calculé
        const maxPercentage = Math.max(calculatedPercentage, data.percentage || 0);
        setProgressPct(maxPercentage);
        
        // Initialiser lastSavedPayloadRef avec les données chargées
        if (data.chapter_id || data.section_id || data.subsection_id) {
          lastSavedPayloadRef.current = {
            chapter_id: data.chapter_id || null,
            section_id: data.section_id || null,
            subsection_id: data.subsection_id || null,
            percentage: maxPercentage,
            position_in_text: data.position_in_text || 0
          };
        }
      } catch (_) {}
    })();
    return () => {
      cancelled = true;
    };
  }, [bookId, bookData?.chapters]);

  const startEdit = (type, ids, initial) => {
    if (!canEditStructure) return;
    setEditStates((prev) => {
      const copy = { ...prev, [type]: { ...prev[type] } };
      const existing = copy[type][ids.id] || {};
      const effectiveTitle =
        typeof existing.title === 'string'
          ? existing.title
          : (initial.title || '');
      const effectiveContent =
        typeof existing.contentOverride === 'string'
          ? existing.contentOverride
          : typeof existing.content === 'string'
          ? existing.content
          : (initial.content || '');

      const baseImages = normalizeImageSlots(
        Array.isArray(existing.imagesOverride)
          ? existing.imagesOverride
          : Array.isArray(initial.images)
          ? initial.images
          : []
      );
      const baseVideos =
        Array.isArray(existing.videosOverride)
          ? existing.videosOverride
          : Array.isArray(initial.videos)
          ? initial.videos
          : [];
      const baseTables =
        Array.isArray(existing.tablesOverride)
          ? existing.tablesOverride
          : Array.isArray(initial.tables)
          ? initial.tables
          : [];

      const imagesText =
        typeof existing.imagesText === 'string'
          ? existing.imagesText
          : baseImages
          ? JSON.stringify(baseImages, null, 2)
          : '[]';
      const videosText =
        typeof existing.videosText === 'string'
          ? existing.videosText
          : baseVideos
          ? JSON.stringify(baseVideos, null, 2)
          : '[]';
      const tablesText =
        typeof existing.tablesText === 'string'
          ? existing.tablesText
          : baseTables
          ? JSON.stringify(baseTables, null, 2)
          : '[]';

      const effectiveIsIntro =
        typeof existing.is_intro_override === 'boolean'
          ? existing.is_intro_override
          : typeof existing.is_intro === 'boolean'
          ? existing.is_intro
          : !!initial.is_intro;

      const effectiveImageDisplayConfig =
        existing.image_display_config || initial.image_display_config || {};

      const effectiveVideoDisplayConfig =
        existing.video_display_config || initial.video_display_config || {};

      copy[type][ids.id] = {
        ...existing,
        editing: true,
        title: effectiveTitle,
        content: effectiveContent,
        imagesText,
        videosText,
        tablesText,
        is_intro: effectiveIsIntro,
        image_display_config: effectiveImageDisplayConfig,
        video_display_config: effectiveVideoDisplayConfig,
      };

      return copy;
    });
  };

  const cancelEdit = (type, id) => {
    setEditStates((prev) => {
      const copy = { ...prev, [type]: { ...prev[type] } };
      copy[type][id] = { editing: false };
      return copy;
    });
  };

  const onChangeEditField = (type, id, field, value) => {
    setEditStates((prev) => {
      const copy = { ...prev, [type]: { ...prev[type] } };
      copy[type][id] = { ...(copy[type][id] || {}), [field]: value };
      return copy;
    });
  };

  const updateDraftImages = (type, id, images) =>
    onChangeEditField(
      type,
      id,
      'imagesText',
      JSON.stringify(images, null, 2)
    );

  const mutationErrorMessage = (error) => {
    const imageError = error?.response?.data?.images;
    if (typeof imageError === 'string') return imageError;
    if (imageError) return JSON.stringify(imageError);
    return (
      error?.response?.data?.detail ||
      error?.response?.data?.error ||
      error?.message ||
      'Erreur inconnue'
    );
  };

  const updateTableValidity = (type, id, tableIndex, isValid) => {
    const key = `${type}-${id}-${tableIndex}`;
    setTableValidity((current) =>
      current[key] === isValid ? current : { ...current, [key]: isValid }
    );
  };

  const hasInvalidTableLayout = (type, id, tables) =>
    tables.some(
      (_, tableIndex) =>
        tableValidity[`${type}-${id}-${tableIndex}`] === false
    );

  const saveChapter = async (chapter) => {
    const st = editStates.chapters[chapter.id];
    if (!st) return;
    
    const loadingToast = toast.loading('Modification du chapitre en cours...', { position: 'top-right' });
    
    try {
      let images = [];
      let videos = [];
      let tables = [];
      try { images = JSON.parse(st.imagesText || '[]'); } catch (_) {}
      try { videos = JSON.parse(st.videosText || '[]'); } catch (_) {}
      try { tables = JSON.parse(st.tablesText || '[]'); } catch (_) {}
      if (hasInvalidTableLayout('chapters', chapter.id, tables)) {
        throw new Error(
          'Corrigez les largeurs de colonnes avant d’enregistrer.'
        );
      }
      const is_intro = !!st.is_intro;
      const title =
        typeof st.title === 'string' && st.title.trim().length > 0
          ? st.title.trim()
          : (chapter.title || '');
      const image_display_config = st.image_display_config || chapter.image_display_config || {};
      const video_display_config = st.video_display_config || chapter.video_display_config || {};
      const params = {};
      if (selectedLang && selectedLang !== 'source') {
        params.lang = selectedLang;
      }
      await api.patch(`/books/${bookId}/chapters/${chapter.id}/`, { title, content: st.content, images, videos, tables, is_intro, image_display_config, video_display_config }, { params });
      setEditStates((prev) => {
        const copy = { ...prev, chapters: { ...prev.chapters } };
        copy.chapters[chapter.id] = {
          editing: false,
          titleOverride: title,
          contentOverride: st.content,
          imagesOverride: images,
          videosOverride: videos,
          tablesOverride: tables,
          is_intro_override: is_intro,
          image_display_config: image_display_config,
          video_display_config: video_display_config,
        };
        return copy;
      });
      
      toast.success('Chapitre modifié avec succès !', { id: loadingToast, duration: 3000, position: 'top-right' });
      
      await refreshAtAnchor([`chapter-${chapter.id}`]);
    } catch (e) {
      console.error('Erreur sauvegarde chapitre', e);
      toast.error(
        <div>
          <div className="font-semibold">Erreur de modification</div>
          <div className="text-sm mt-1">{mutationErrorMessage(e)}</div>
        </div>,
        { id: loadingToast, duration: 5000, position: 'top-right' }
      );
    }
  };

  const saveSection = async (chapter, section) => {
    const st = editStates.sections[section.id];
    if (!st) return;
    
    const loadingToast = toast.loading('Modification de la section en cours...', { position: 'top-right' });
    
    try {
      let images = [];
      let videos = [];
      let tables = [];
      try { images = JSON.parse(st.imagesText || '[]'); } catch (_) {}
      try { videos = JSON.parse(st.videosText || '[]'); } catch (_) {}
      try { tables = JSON.parse(st.tablesText || '[]'); } catch (_) {}
      if (hasInvalidTableLayout('sections', section.id, tables)) {
        throw new Error(
          'Corrigez les largeurs de colonnes avant d’enregistrer.'
        );
      }
      const title =
        typeof st.title === 'string' && st.title.trim().length > 0
          ? st.title.trim()
          : (section.title || '');
      const image_display_config = st.image_display_config || section.image_display_config || {};
      const video_display_config = st.video_display_config || section.video_display_config || {};
      const params = {};
      if (selectedLang && selectedLang !== 'source') {
        params.lang = selectedLang;
      }
      await api.patch(`/books/${bookId}/chapters/${chapter.id}/sections/${section.id}/`, {
        title,
        content: st.content,
        images,
        videos,
        tables,
        image_display_config,
        video_display_config,
      }, { params });
      setEditStates((prev) => {
        const copy = { ...prev, sections: { ...prev.sections } };
        copy.sections[section.id] = {
          editing: false,
          titleOverride: title,
          contentOverride: st.content,
          imagesOverride: images,
          videosOverride: videos,
          tablesOverride: tables,
          image_display_config: image_display_config,
          video_display_config: video_display_config,
        };
        return copy;
      });
      
      toast.success('Section modifiée avec succès !', { id: loadingToast, duration: 3000, position: 'top-right' });
      
      await refreshAtAnchor([
        `section-${section.id}`,
        `chapter-${chapter.id}`,
      ]);
    } catch (e) {
      console.error('Erreur sauvegarde section', e);
      toast.error(
        <div>
          <div className="font-semibold">Erreur de modification</div>
          <div className="text-sm mt-1">{mutationErrorMessage(e)}</div>
        </div>,
        { id: loadingToast, duration: 5000, position: 'top-right' }
      );
    }
  };

  const saveSubsection = async (chapter, section, subsection) => {
    const st = editStates.subsections[subsection.id];
    if (!st) return;
    
    const loadingToast = toast.loading('Modification de la sous-section en cours...', { position: 'top-right' });
    
    try {
      let images = [];
      let videos = [];
      let tables = [];
      try { images = JSON.parse(st.imagesText || '[]'); } catch (_) {}
      try { videos = JSON.parse(st.videosText || '[]'); } catch (_) {}
      try { tables = JSON.parse(st.tablesText || '[]'); } catch (_) {}
      if (hasInvalidTableLayout('subsections', subsection.id, tables)) {
        throw new Error(
          'Corrigez les largeurs de colonnes avant d’enregistrer.'
        );
      }
      const title =
        typeof st.title === 'string' && st.title.trim().length > 0
          ? st.title.trim()
          : (subsection.title || '');
      const image_display_config = st.image_display_config || subsection.image_display_config || {};
      const video_display_config = st.video_display_config || subsection.video_display_config || {};
      const params = {};
      if (selectedLang && selectedLang !== 'source') {
        params.lang = selectedLang;
      }
      await api.patch(`/books/${bookId}/chapters/${chapter.id}/sections/${section.id}/subsections/${subsection.id}/`, {
        title,
        content: st.content,
        images,
        videos,
        tables,
        image_display_config,
        video_display_config,
      }, { params });
      setEditStates((prev) => {
        const copy = { ...prev, subsections: { ...prev.subsections } };
        copy.subsections[subsection.id] = {
          editing: false,
          titleOverride: title,
          contentOverride: st.content,
          imagesOverride: images,
          videosOverride: videos,
          tablesOverride: tables,
          image_display_config: image_display_config,
          video_display_config: video_display_config,
        };
        return copy;
      });
      
      toast.success('Sous-section modifiée avec succès !', { id: loadingToast, duration: 3000, position: 'top-right' });
      
      await refreshAtAnchor([
        `subsection-${subsection.id}`,
        `section-${section.id}`,
        `chapter-${chapter.id}`,
      ]);
    } catch (e) {
      console.error('Erreur sauvegarde sous-section', e);
      toast.error(
        <div>
          <div className="font-semibold">Erreur de modification</div>
          <div className="text-sm mt-1">{mutationErrorMessage(e)}</div>
        </div>,
        { id: loadingToast, duration: 5000, position: 'top-right' }
      );
    }
  };

  // Création de nouveaux chapitres / sections / sous-sections
  const startCreateChapter = () => {
    if (
      !canEditStructure ||
      activeCreateTarget ||
      createStates.chapter.open
    ) {
      return;
    }
    setCreateStates((prev) => ({
      ...prev,
      chapter: { open: true, title: '', content: '', images: [], tables: [], position: 'first', afterId: '' },
    }));
    setCreateChapterTableValidity({});
  };

  const cancelCreateChapter = () => {
    setCreateStates((prev) => ({
      ...prev,
      chapter: { open: false, title: '', content: '', images: [], tables: [], position: 'first', afterId: '' },
    }));
    setCreateChapterTableValidity({});
  };

  const saveCreateChapter = async () => {
    if (!bookId) return;
    const { title, content, images, tables, position, afterId } = createStates.chapter;
    if (!title || !title.trim()) {
      toast.error('Le titre du chapitre est requis', { position: 'top-right', duration: 3000 });
      return;
    }
    
    const loadingToast = toast.loading('Création du chapitre en cours...', { position: 'top-right' });
    
    try {
      const payload = {
        title: title.trim(),
        content: content || '',
        images: images || [],
        tables: tables || [],
      };
      if (position) payload.position = position;
      if (position === 'after' && afterId) payload.after_id = afterId;
      await api.post(`/books/${bookId}/chapters/`, payload);
      
      toast.success('Chapitre créé avec succès !', { id: loadingToast, duration: 3000, position: 'top-right' });
      
      cancelCreateChapter();
      if (typeof onBookContentChanged === 'function') {
        await onBookContentChanged();
      }
    } catch (e) {
      console.error('Erreur lors de la création du chapitre', e);
      toast.error(
        <div>
          <div className="font-semibold">Erreur de création</div>
          <div className="text-sm mt-1">{e?.response?.data?.error || e?.message || 'Erreur inconnue'}</div>
        </div>,
        { id: loadingToast, duration: 5000, position: 'top-right' }
      );
    }
  };

  // Fonctions de suppression
  const deleteChapter = async (chapterId) => {
    if (!bookId || !canEditStructure) return;
    
    const confirmed = await new Promise((resolve) => {
      toast((toastObj) => (
        <div className="flex flex-col gap-3">
          <div>
            <div className="font-semibold text-gray-900 mb-1">Supprimer ce chapitre ?</div>
            <div className="text-sm text-gray-600">Cette action est irréversible. Toutes les sections et sous-sections seront également supprimées.</div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => { toast.dismiss(toastObj.id); resolve(false); }}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={() => { toast.dismiss(toastObj.id); resolve(true); }}
              className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
            >
              Supprimer
            </button>
          </div>
        </div>
      ), {
        duration: Infinity,
        position: 'top-center',
        style: { maxWidth: '500px', padding: '16px' },
      });
    });
    
    if (!confirmed) return;
    
    const loadingToast = toast.loading('Suppression du chapitre en cours...', { position: 'top-right' });
    
    try {
      await api.delete(`/books/${bookId}/chapters/${chapterId}/`);
      
      toast.success('Chapitre supprimé avec succès !', { id: loadingToast, duration: 3000, position: 'top-right' });
      
      if (typeof onBookContentChanged === 'function') {
        await onBookContentChanged();
      }
    } catch (e) {
      console.error('Erreur lors de la suppression du chapitre', e);
      toast.error(
        <div>
          <div className="font-semibold">Erreur de suppression</div>
          <div className="text-sm mt-1">{e?.response?.data?.error || e?.message || 'Erreur inconnue'}</div>
        </div>,
        { id: loadingToast, duration: 5000, position: 'top-right' }
      );
    }
  };

  const deleteSection = async (chapterId, sectionId) => {
    if (!bookId || !canEditStructure) return;
    
    const confirmed = await new Promise((resolve) => {
      toast((toastObj) => (
        <div className="flex flex-col gap-3">
          <div>
            <div className="font-semibold text-gray-900 mb-1">Supprimer cette section ?</div>
            <div className="text-sm text-gray-600">Cette action est irréversible. Toutes les sous-sections seront également supprimées.</div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => { toast.dismiss(toastObj.id); resolve(false); }}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={() => { toast.dismiss(toastObj.id); resolve(true); }}
              className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
            >
              Supprimer
            </button>
          </div>
        </div>
      ), {
        duration: Infinity,
        position: 'top-center',
        style: { maxWidth: '500px', padding: '16px' },
      });
    });
    
    if (!confirmed) return;
    
    const loadingToast = toast.loading('Suppression de la section en cours...', { position: 'top-right' });
    
    try {
      await api.delete(`/books/${bookId}/chapters/${chapterId}/sections/${sectionId}/`);
      
      toast.success('Section supprimée avec succès !', { id: loadingToast, duration: 3000, position: 'top-right' });
      
      if (typeof onBookContentChanged === 'function') {
        await onBookContentChanged();
      }
    } catch (e) {
      console.error('Erreur lors de la suppression de la section', e);
      toast.error(
        <div>
          <div className="font-semibold">Erreur de suppression</div>
          <div className="text-sm mt-1">{e?.response?.data?.error || e?.message || 'Erreur inconnue'}</div>
        </div>,
        { id: loadingToast, duration: 5000, position: 'top-right' }
      );
    }
  };

  const deleteSubsection = async (chapterId, sectionId, subsectionId) => {
    if (!bookId || !canEditStructure) return;
    
    const confirmed = await new Promise((resolve) => {
      toast((toastObj) => (
        <div className="flex flex-col gap-3">
          <div>
            <div className="font-semibold text-gray-900 mb-1">Supprimer cette sous-section ?</div>
            <div className="text-sm text-gray-600">Cette action est irréversible.</div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => { toast.dismiss(toastObj.id); resolve(false); }}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={() => { toast.dismiss(toastObj.id); resolve(true); }}
              className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
            >
              Supprimer
            </button>
          </div>
        </div>
      ), {
        duration: Infinity,
        position: 'top-center',
        style: { maxWidth: '500px', padding: '16px' },
      });
    });
    
    if (!confirmed) return;
    
    const loadingToast = toast.loading('Suppression de la sous-section en cours...', { position: 'top-right' });
    
    try {
      await api.delete(`/books/${bookId}/chapters/${chapterId}/sections/${sectionId}/subsections/${subsectionId}/`);
      
      toast.success('Sous-section supprimée avec succès !', { id: loadingToast, duration: 3000, position: 'top-right' });
      
      if (typeof onBookContentChanged === 'function') {
        await onBookContentChanged();
      }
    } catch (e) {
      console.error('Erreur lors de la suppression de la sous-section', e);
      toast.error(
        <div>
          <div className="font-semibold">Erreur de suppression</div>
          <div className="text-sm mt-1">{e?.response?.data?.error || e?.message || 'Erreur inconnue'}</div>
        </div>,
        { id: loadingToast, duration: 5000, position: 'top-right' }
      );
    }
  };

  // Fonction pour gérer l'upload de la photo de couverture
  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !bookId) return;

    // Vérifier que c'est bien une image
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner un fichier image', { position: 'top-right', duration: 3000 });
      return;
    }

    setUploadingCover(true);
    const loadingToast = toast.loading('Upload de la couverture en cours...', { position: 'top-right' });

    try {
      const formData = new FormData();
      formData.append('cover_image', file);

      await api.patch(`/books/${bookId}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Photo de couverture mise à jour !', { 
        id: loadingToast, 
        duration: 3000, 
        position: 'top-right' 
      });

      // Recharger les données du livre
      if (typeof onBookContentChanged === 'function') {
        await onBookContentChanged();
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload de la couverture:', error);
      toast.error(
        <div>
          <div className="font-semibold">Erreur d'upload</div>
          <div className="text-sm mt-1">{error?.response?.data?.error || error?.message || 'Erreur inconnue'}</div>
        </div>,
        { id: loadingToast, duration: 5000, position: 'top-right' }
      );
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) {
        coverInputRef.current.value = '';
      }
    }
  };

  // Fonctions de drag & drop
  const handleDragStart = (e, type, id, chapterId = null, sectionId = null) => {
    if (!dragDropMode) return;
    setDraggedItem({ type, id, chapterId, sectionId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    if (!dragDropMode || !draggedItem) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropChapter = async (e, targetChapterId) => {
    e.preventDefault();
    if (!dragDropMode || !draggedItem || !bookId) return;

    const loadingToast = toast.loading('Déplacement en cours...', { position: 'top-right' });

    try {
      if (draggedItem.type === 'chapter' && draggedItem.id !== targetChapterId) {
        // Déplacer un chapitre
        const chapters = bookData?.chapters || [];
        const fromIdx = chapters.findIndex(c => c.id === draggedItem.id);
        const toIdx = chapters.findIndex(c => c.id === targetChapterId);
        
        if (fromIdx !== -1 && toIdx !== -1) {
          await api.patch(`/books/${bookId}/chapters/${draggedItem.id}/`, { order: toIdx });
          toast.success('Chapitre déplacé !', { id: loadingToast, duration: 2000, position: 'top-right' });
          if (typeof onBookContentChanged === 'function') {
            await onBookContentChanged();
          }
        } else {
          toast.dismiss(loadingToast);
        }
      } else if (draggedItem.type === 'section') {
        // Déplacer une section vers un chapitre
        await api.patch(`/books/${bookId}/chapters/${draggedItem.chapterId}/sections/${draggedItem.id}/move/`, {
          target_chapter_id: targetChapterId
        });
        toast.success('Section déplacée !', { id: loadingToast, duration: 2000, position: 'top-right' });
        if (typeof onBookContentChanged === 'function') {
          await onBookContentChanged();
        }
      } else if (draggedItem.type === 'subsection') {
        toast.dismiss(loadingToast);
        toast.error('Déposez la sous-section sur une section', { duration: 3000, position: 'top-right' });
      } else {
        toast.dismiss(loadingToast);
      }
    } catch (error) {
      console.error('Erreur lors du déplacement:', error);
      toast.error('Erreur lors du déplacement', { id: loadingToast, duration: 3000, position: 'top-right' });
    }
    
    setDraggedItem(null);
  };

  const handleDropSection = async (e, targetChapterId, targetSectionId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragDropMode || !draggedItem || !bookId) return;

    console.log('🔍 handleDropSection appelé:', {
      draggedItem,
      targetChapterId,
      targetSectionId,
      dragDropMode,
      bookId
    });

    const loadingToast = toast.loading('Déplacement en cours...', { position: 'top-right' });

    try {
      // console.log('🔍 Vérification des conditions:', {
      //   'draggedItem.type': draggedItem.type,
      //   'draggedItem.id': draggedItem.id,
      //   'targetSectionId': targetSectionId,
      //   'type === section': draggedItem.type === 'section',
      //   'id !== targetSectionId': draggedItem.id !== targetSectionId,
      //   'condition complète': draggedItem.type === 'section' && draggedItem.id !== targetSectionId
      // });

      if (draggedItem.type === 'section' && draggedItem.id !== targetSectionId) {
        // console.log('✅ Condition section satisfaite, envoi de la requête...');
        // Déplacer une section dans le même chapitre ou vers un autre
        const chapter = bookData?.chapters?.find(c => c.id === targetChapterId);
        const sections = chapter?.sections || [];
        const toIdx = sections.findIndex(s => s.id === targetSectionId);
        
        if (draggedItem.chapterId === targetChapterId) {
          // Même chapitre - réordonner
          await api.patch(`/books/${bookId}/chapters/${targetChapterId}/sections/${draggedItem.id}/`, { order: toIdx });
        } else {
          // Chapitre différent - déplacer
          await api.patch(`/books/${bookId}/chapters/${draggedItem.chapterId}/sections/${draggedItem.id}/move/`, {
            target_chapter_id: targetChapterId,
            order: toIdx
          });
        }
        toast.success('Section déplacée !', { id: loadingToast, duration: 2000, position: 'top-right' });
        if (typeof onBookContentChanged === 'function') {
          await onBookContentChanged();
        }
      } else if (draggedItem.type === 'subsection') {
        // Déplacer une sous-section vers une section
        await api.patch(`/books/${bookId}/chapters/${draggedItem.chapterId}/sections/${draggedItem.sectionId}/subsections/${draggedItem.id}/move/`, {
          target_section_id: targetSectionId
        });
        toast.success('Sous-section déplacée !', { id: loadingToast, duration: 2000, position: 'top-right' });
        if (typeof onBookContentChanged === 'function') {
          await onBookContentChanged();
        }
      } else {
        // Aucune action à effectuer (drop sur soi-même ou type incompatible)
        toast.dismiss(loadingToast);
      }
    } catch (error) {
      console.error('Erreur lors du déplacement:', error);
      toast.error('Erreur lors du déplacement', { id: loadingToast, duration: 3000, position: 'top-right' });
    }
    
    setDraggedItem(null);
  };

  const handleDropSubsection = async (e, targetChapterId, targetSectionId, targetSubsectionId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragDropMode || !draggedItem || !bookId) return;
    if (draggedItem.type !== 'subsection' || draggedItem.id === targetSubsectionId) return;

    const loadingToast = toast.loading('Déplacement en cours...', { position: 'top-right' });

    try {
      const section = bookData?.chapters?.find(c => c.id === targetChapterId)?.sections?.find(s => s.id === targetSectionId);
      const subsections = section?.subsections || [];
      const toIdx = subsections.findIndex(sub => sub.id === targetSubsectionId);
      
      if (draggedItem.sectionId === targetSectionId) {
        // Même section - réordonner
        await api.patch(`/books/${bookId}/chapters/${targetChapterId}/sections/${targetSectionId}/subsections/${draggedItem.id}/`, { order: toIdx });
      } else {
        // Section différente - déplacer
        await api.patch(`/books/${bookId}/chapters/${draggedItem.chapterId}/sections/${draggedItem.sectionId}/subsections/${draggedItem.id}/move/`, {
          target_section_id: targetSectionId,
          order: toIdx
        });
      }
      toast.success('Sous-section déplacée !', { id: loadingToast, duration: 2000, position: 'top-right' });
      if (typeof onBookContentChanged === 'function') {
        await onBookContentChanged();
      }
    } catch (error) {
      console.error('Erreur lors du déplacement:', error);
      toast.error('Erreur lors du déplacement', { id: loadingToast, duration: 3000, position: 'top-right' });
    }
    
    setDraggedItem(null);
  };

  // Fonctions pour déplacer les chapitres avec les flèches haut/bas
  const moveChapterUp = async (chapterId) => {
    if (!bookId || !bookData?.chapters) return;
    
    const chapters = bookData.chapters;
    const currentIndex = chapters.findIndex(c => c.id === chapterId);
    if (currentIndex <= 0) return;
    
    // Créer le nouvel ordre des chapitres
    const newOrder = chapters.map(c => c.id);
    [newOrder[currentIndex - 1], newOrder[currentIndex]] = [newOrder[currentIndex], newOrder[currentIndex - 1]];
    
    const loadingToast = toast.loading('Déplacement en cours...', { position: 'top-right' });
    
    try {
      await api.post(`/books/${bookId}/reorder-chapters/`, { chapter_ids: newOrder });
      toast.success('Chapitre déplacé !', { id: loadingToast, duration: 2000, position: 'top-right' });
      if (typeof onBookContentChanged === 'function') {
        await onBookContentChanged();
      }
    } catch (error) {
      console.error('Erreur lors du déplacement:', error);
      toast.error('Erreur lors du déplacement', { id: loadingToast, duration: 3000, position: 'top-right' });
    }
  };

  const moveChapterDown = async (chapterId) => {
    if (!bookId || !bookData?.chapters) return;
    
    const chapters = bookData.chapters;
    const currentIndex = chapters.findIndex(c => c.id === chapterId);
    if (currentIndex === -1 || currentIndex >= chapters.length - 1) return;
    
    // Créer le nouvel ordre des chapitres
    const newOrder = chapters.map(c => c.id);
    [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]];
    
    const loadingToast = toast.loading('Déplacement en cours...', { position: 'top-right' });
    
    try {
      await api.post(`/books/${bookId}/reorder-chapters/`, { chapter_ids: newOrder });
      toast.success('Chapitre déplacé !', { id: loadingToast, duration: 2000, position: 'top-right' });
      if (typeof onBookContentChanged === 'function') {
        await onBookContentChanged();
      }
    } catch (error) {
      console.error('Erreur lors du déplacement:', error);
      toast.error('Erreur lors du déplacement', { id: loadingToast, duration: 3000, position: 'top-right' });
    }
  };

  const moveRootSubsection = async (
    chapterId,
    sectionId,
    subsectionId,
    targetOrder
  ) => {
    if (!bookId || targetOrder < 0) return;

    const loadingToast = toast.loading('Déplacement en cours...', {
      position: 'top-right',
    });
    try {
      await api.patch(
        `/books/${bookId}/chapters/${chapterId}/sections/${sectionId}/subsections/${subsectionId}/move/`,
        {
          target_section_id: sectionId,
          target_parent_id: null,
          order: targetOrder,
        }
      );
      toast.success('Sous-section déplacée !', {
        id: loadingToast,
        duration: 2000,
        position: 'top-right',
      });
      if (typeof onBookContentChanged === 'function') {
        await onBookContentChanged();
      }
    } catch (error) {
      console.error('Erreur lors du déplacement:', error);
      toast.error('Erreur lors du déplacement', {
        id: loadingToast,
        duration: 3000,
        position: 'top-right',
      });
    }
  };

  // DÉSACTIVÉ - Fonctions pour déplacer les sections/sous-sections avec les flèches
  // Ces fonctions ne fonctionnent pas correctement, seul le drag & drop des chapitres est actif
  /*
  const moveSectionUp = (chapterId, sectionId) => {
    if (!localBookData) return;
    
    const newData = JSON.parse(JSON.stringify(localBookData));
    const chapter = newData.chapters?.find(c => c.id === chapterId);
    if (!chapter || !chapter.sections) return;
    
    const sectionIndex = chapter.sections.findIndex(s => s.id === sectionId);
    if (sectionIndex <= 0) return;
    
    [chapter.sections[sectionIndex - 1], chapter.sections[sectionIndex]] = 
    [chapter.sections[sectionIndex], chapter.sections[sectionIndex - 1]];
    
    setLocalBookData(newData);
    setHasUnsavedChanges(true);
  };

  const moveSectionDown = (chapterId, sectionId) => {
    if (!localBookData) return;
    
    const newData = JSON.parse(JSON.stringify(localBookData));
    const chapter = newData.chapters?.find(c => c.id === chapterId);
    if (!chapter || !chapter.sections) return;
    
    const sectionIndex = chapter.sections.findIndex(s => s.id === sectionId);
    if (sectionIndex === -1 || sectionIndex >= chapter.sections.length - 1) return;
    
    [chapter.sections[sectionIndex], chapter.sections[sectionIndex + 1]] = 
    [chapter.sections[sectionIndex + 1], chapter.sections[sectionIndex]];
    
    setLocalBookData(newData);
    setHasUnsavedChanges(true);
  };

  const moveSubsectionUp = (chapterId, sectionId, subsectionId) => {
    if (!localBookData) return;
    
    const newData = JSON.parse(JSON.stringify(localBookData));
    const chapter = newData.chapters?.find(c => c.id === chapterId);
    if (!chapter) return;
    
    const section = chapter.sections?.find(s => s.id === sectionId);
    if (!section || !section.subsections) return;
    
    const subsectionIndex = section.subsections.findIndex(sub => sub.id === subsectionId);
    if (subsectionIndex <= 0) return;
    
    [section.subsections[subsectionIndex - 1], section.subsections[subsectionIndex]] = 
    [section.subsections[subsectionIndex], section.subsections[subsectionIndex - 1]];
    
    setLocalBookData(newData);
    setHasUnsavedChanges(true);
  };

  const moveSubsectionDown = (chapterId, sectionId, subsectionId) => {
    if (!localBookData) return;
    
    const newData = JSON.parse(JSON.stringify(localBookData));
    const chapter = newData.chapters?.find(c => c.id === chapterId);
    if (!chapter) return;
    
    const section = chapter.sections?.find(s => s.id === sectionId);
    if (!section || !section.subsections) return;
    
    const subsectionIndex = section.subsections.findIndex(sub => sub.id === subsectionId);
    if (subsectionIndex === -1 || subsectionIndex >= section.subsections.length - 1) return;
    
    [section.subsections[subsectionIndex], section.subsections[subsectionIndex + 1]] = 
    [section.subsections[subsectionIndex + 1], section.subsections[subsectionIndex]];
    
    setLocalBookData(newData);
    setHasUnsavedChanges(true);
  };

  const saveOrderChanges = async () => {
    if (!localBookData || !bookId) return;
    
    const loadingToast = toast.loading('Sauvegarde des modifications...', { position: 'top-right' });
    
    try {
      const updates = [];
      
      localBookData.chapters?.forEach((chapter) => {
        chapter.sections?.forEach((section, index) => {
          updates.push(
            api.patch(`/books/${bookId}/chapters/${chapter.id}/sections/${section.id}/`, { order: index })
          );
          
          section.subsections?.forEach((subsection, subIndex) => {
            updates.push(
              api.patch(`/books/${bookId}/chapters/${chapter.id}/sections/${section.id}/subsections/${subsection.id}/`, { order: subIndex })
            );
          });
        });
      });
      
      await Promise.all(updates);
      
      toast.success('Modifications sauvegardées !', { id: loadingToast, duration: 2000, position: 'top-right' });
      setHasUnsavedChanges(false);
      
      if (typeof onBookContentChanged === 'function') {
        await onBookContentChanged();
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde', { id: loadingToast, duration: 3000, position: 'top-right' });
    }
  };
  */

  const saveProgress = (payload) => {
    if (!bookId) {
      // console.log('⚠️ Pas de bookId, sauvegarde annulée');
      return;
    }
    
    // Vérifier si le chapitre/section/subsection a changé
    const lastSaved = lastSavedPayloadRef.current;
    if (lastSaved) {
      // Comparer les IDs en tenant compte des valeurs undefined/null
      const sameChapter = (lastSaved.chapter_id || null) === (payload.chapter_id || null);
      const sameSection = (lastSaved.section_id || null) === (payload.section_id || null);
      const sameSubsection = (lastSaved.subsection_id || null) === (payload.subsection_id || null);
      
      // console.log('🔍 Comparaison avec dernière sauvegarde:', {
      //   lastSaved,
      //   payload,
      //   sameChapter,
      //   sameSection,
      //   sameSubsection
      // });
      
      // Si on est toujours sur exactement le même élément, ne pas envoyer
      if (sameChapter && sameSection && sameSubsection) {
        // console.log('⏭️ Même position, pas de sauvegarde');
        return;
      }
    }
    
    // Sauvegarder le payload et envoyer la requête
    lastSavedPayloadRef.current = { ...payload };
    console.log('📚 Sauvegarde progression:', payload);
    api.patch(`/books/${bookId}/reading-progress/`, payload).catch((err) => {
      console.error('❌ Erreur sauvegarde:', err);
    });
  };

  const IDLE_MS = 2500;   // temps sans scroll avant de considérer la position stable
  const DWELL_MS = 6000;  // temps minimum passé sur la même ancre (chapitre/section/sous-section)

  const scheduleSaveCheck = () => {
    if (saveCheckTimerRef.current) clearTimeout(saveCheckTimerRef.current);
    saveCheckTimerRef.current = setTimeout(() => {
      const now = Date.now();
      const idleOk = now - (lastScrollTsRef.current || 0) >= IDLE_MS;
      const a = anchorRef.current || { chapter_id: null, section_id: null, subsection_id: null };
      
      // Calculer le temps de lecture nécessaire basé sur le chapitre/section/subsection
      let dwellNeeded = DWELL_MS;
      if (a.subsection_id) {
        const est = estimateReadMs('subsection', a.subsection_id);
        dwellNeeded = Math.floor(READ_RATIO * est);
      } else if (a.section_id) {
        const est = estimateReadMs('section', a.section_id);
        dwellNeeded = Math.floor(READ_RATIO * est);
      } else if (a.chapter_id) {
        const est = estimateReadMs('chapter', a.chapter_id);
        dwellNeeded = Math.floor(READ_RATIO * est);
      }
      
      const dwellOk = now - (anchorChangedTsRef.current || 0) >= dwellNeeded;
      
      // console.log('⏱️ Vérification sauvegarde:', {
      //   idleOk,
      //   dwellOk,
      //   dwellNeeded: Math.round(dwellNeeded / 1000) + 's',
      //   hasPending: !!pendingPayloadRef.current,
      //   anchor: a
      // });
      
      if (idleOk && dwellOk && pendingPayloadRef.current) {
        saveProgress(pendingPayloadRef.current);
      } else {
        // Replanifier jusqu'à ce que les conditions soient remplies
        scheduleSaveCheck();
      }
    }, 400);
  };

  const computeAndSaveProgress = (isRealScroll = true) => {
    if (!bookId) return;
    const now = Date.now();
    const doc = document.documentElement;
    // Amélioration de la détection du scroll pour mobile
    const scrollTop = window.pageYOffset || window.scrollY || doc.scrollTop || document.body.scrollTop || 0;
    const scrollHeight = Math.max(doc.scrollHeight, document.body.scrollHeight) || 1;
    const clientHeight = window.innerHeight || doc.clientHeight || document.body.clientHeight || 1;
    const pct = Math.max(0, Math.min(100, (scrollTop / Math.max(1, scrollHeight - clientHeight)) * 100));
    // Ne pas mettre à jour le pourcentage automatiquement - seulement lors d'un changement de chapitre
    // setProgressPct(pct);

    const threshold = 120;
    
    // Fonction pour trouver le dernier élément visible (avec r.top <= threshold)
    const findLastVisible = (selector) => {
      const nodes = Array.from(document.querySelectorAll(selector));
      let lastId = null;
      for (let i = 0; i < nodes.length; i++) {
        const r = nodes[i].getBoundingClientRect();
        if (r.top <= threshold) {
          lastId = parseInt(nodes[i].id.split('-').pop(), 10) || null;
        }
      }
      return lastId;
    };

    // 1. D'abord trouver le chapitre actuel
    const currentChapterId = findLastVisible('[id^="chapter-"]');
    
    // 2. Trouver les sections/subsections uniquement dans le chapitre actuel
    let currentSectionId = null;
    let currentSubsectionId = null;
    
    if (currentChapterId && bookData?.chapters) {
      const chapter = bookData.chapters.find(c => c.id === currentChapterId);
      
      if (chapter?.sections?.length > 0) {
        // Chercher la section actuelle dans ce chapitre
        for (const section of chapter.sections) {
          const sectionEl = document.getElementById(`section-${section.id}`);
          if (sectionEl) {
            const r = sectionEl.getBoundingClientRect();
            if (r.top <= threshold) {
              currentSectionId = section.id;
              
              // Chercher la subsection actuelle dans cette section
              if (section.subsections?.length > 0) {
                for (const subsection of flattenSubsections(section.subsections)) {
                  const subEl = document.getElementById(`subsection-${subsection.id}`);
                  if (subEl) {
                    const r2 = subEl.getBoundingClientRect();
                    if (r2.top <= threshold) {
                      currentSubsectionId = subsection.id;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    const payload = { 
      position_in_text: Math.floor(scrollTop),
      chapter_id: currentChapterId || null,
      section_id: currentSectionId || null,
      subsection_id: currentSubsectionId || null
    };

    // console.log('📍 Position détectée:', {
    //   chapter_id: currentChapterId,
    //   section_id: currentSectionId,
    //   subsection_id: currentSubsectionId,
    //   payload
    // });

    // Ne sauvegarder que si on a au moins un chapter_id
    if (!payload.chapter_id && !payload.section_id && !payload.subsection_id) {
      // console.warn('⚠️ Aucun élément détecté, pas de sauvegarde');
      return;
    }

    // Toujours calculer le pourcentage basé sur le chapitre actuel
    const newPercentage = calculateRealPercentage(
      payload.chapter_id,
      payload.section_id,
      payload.subsection_id
    );
    
    // Le pourcentage ne doit jamais diminuer (high water mark)
    const currentMaxPercentage = Math.max(
      newPercentage,
      lastSavedPayloadRef.current?.percentage || 0
    );
    payload.percentage = currentMaxPercentage;

    // Mettre à jour l'ancre courante et le timestamp de changement
    const prev = anchorRef.current || { chapter_id: null, section_id: null, subsection_id: null };
    const hasChanged = prev.chapter_id !== currentChapterId || 
                       prev.section_id !== currentSectionId || 
                       prev.subsection_id !== currentSubsectionId;
    
    // Mettre à jour l'affichage du pourcentage (seulement si supérieur)
    setProgressPct(prev => Math.max(prev, newPercentage));
    
    if (hasChanged) {
      anchorRef.current = { 
        chapter_id: currentChapterId, 
        section_id: currentSectionId, 
        subsection_id: currentSubsectionId 
      };
      anchorChangedTsRef.current = now;
      
      // console.log('🔄 Changement détecté:', {
      //   prev,
      //   new: { chapter_id: currentChapterId, section_id: currentSectionId, subsection_id: currentSubsectionId },
      //   percentage: newPercentage
      // });
    }

    // Mémoriser la dernière activité de scroll et le payload à sauvegarder
    if (isRealScroll) {
      lastScrollTsRef.current = now;
    }
    pendingPayloadRef.current = payload;
    scheduleSaveCheck();
  };

  useEffect(() => {
    const handler = () => computeAndSaveProgress();
    
    // Écouter les événements de scroll sur plusieurs éléments pour une meilleure compatibilité mobile
    window.addEventListener('scroll', handler, { passive: true });
    document.addEventListener('scroll', handler, { passive: true });
    
    // Écouter également les événements tactiles pour les appareils mobiles
    window.addEventListener('touchmove', handler, { passive: true });
    document.addEventListener('touchmove', handler, { passive: true });
    
    // Déclencher un calcul initial après le chargement
    setTimeout(handler, 500);
    
    // Calculer périodiquement la progression pour s'assurer qu'elle est mise à jour
    // Passer false pour ne pas réinitialiser le timer de scroll
    const periodicCheck = setInterval(() => computeAndSaveProgress(false), 2000);
    
    
    return () => {
      window.removeEventListener('scroll', handler);
      document.removeEventListener('scroll', handler);
      window.removeEventListener('touchmove', handler);
      document.removeEventListener('touchmove', handler);
      clearInterval(periodicCheck);
      if (progressTimerRef.current) clearTimeout(progressTimerRef.current);
      if (saveCheckTimerRef.current) clearTimeout(saveCheckTimerRef.current);
    };
  }, [bookId]);

  // Regrouper les chapitres par thématique pour l'affichage "full content"
  // Utiliser localBookData si disponible, sinon bookData
  const displayData = localBookData || bookData;

  const groupedChapters = useMemo(() => {
    if (!Array.isArray(displayData?.chapters)) return [];
    const map = new Map(); // préserve l'ordre d'apparition
    const WITHOUT = 'sans-thematique';
    for (const ch of displayData.chapters) {
      const key = ch.thematique ? String(ch.thematique.id) : WITHOUT;
      if (!map.has(key)) {
        map.set(key, { key, thematique: ch.thematique || null, chapters: [] });
      }
      map.get(key).chapters.push(ch);
    }
    return Array.from(map.values());
  }, [displayData?.chapters]);

  const handleChapterDrop = async (e, targetChapterId) => {
    if (!canEditStructure || !bookId) return;
    if (dragState.type !== 'chapter' || !dragState.id) return;
    e.preventDefault();

    const draggedId = dragState.id;
    if (draggedId === targetChapterId) {
      setDragState({ type: null, id: null });
      return;
    }

    const chaptersAll = Array.isArray(bookData?.chapters) ? bookData.chapters : [];
    if (!chaptersAll.length) return;

    const sorted = [...chaptersAll].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const fromIndex = sorted.findIndex((ch) => ch.id === draggedId);
    const toIndex = sorted.findIndex((ch) => ch.id === targetChapterId);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
      setDragState({ type: null, id: null });
      return;
    }

    const updated = [...sorted];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);

    try {
      await Promise.all(
        updated.map((ch, idx) =>
          api.patch(`/books/${bookId}/chapters/${ch.id}/`, { order: idx })
        )
      );
      setDragState({ type: null, id: null });
      if (typeof onBookContentChanged === 'function') {
        await onBookContentChanged();
      }
    } catch (error) {
      console.error('Erreur lors de la réorganisation des chapitres:', error);
      setDragState({ type: null, id: null });
    }
  };

  return (
    <div className="box-border min-w-0 max-w-full px-3 pr-4 py-4 sm:px-6 sm:pr-8 sm:py-8" ref={contentRef}>
      <Toaster />
      <ImageLightbox
        open={lightbox.open}
        src={lightbox.src}
        alt={lightbox.alt}
        caption={lightbox.caption}
        onClose={() => setLightbox({ open: false, src: '', alt: '', caption: '' })}
      />
      {/* Introduction du livre - Fixe lors du défilement */}
      {displayTitle && (
        <div className="w-full mb-6 sm:mb-8 sticky top-0 bg-white z-10 py-3 sm:py-4 border-b border-gray-200">
          <div className="flex items-start justify-between gap-3">
            {!editMode ? (
              <h1
                id="book-title"
                className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4"
              >
                {displayTitle}
              </h1>
            ) : (
              <div className="flex flex-col gap-2 w-full max-w-2xl">
                <input
                  type="text"
                  value={pendingTitle}

                  onChange={(e) => setPendingTitle(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 text-2xl font-bold"
                />
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                    onClick={async () => {
                      try {
                        if (!bookId) return;
                        await api.patch(`/books/${bookId}/`, { title: pendingTitle });
                        setDisplayTitle(pendingTitle);
                        setEditMode(false);
                      } catch (e) {
                        console.error('Erreur lors de la mise à jour du titre', e);
                      }
                    }}
                  >
                    Enregistrer
                  </button>
                  <button
                    className="px-3 py-1.5 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                    onClick={() => { setPendingTitle(displayTitle); setEditMode(false); }}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}
            {isAdmin && !editMode && (
              <div className="flex items-center gap-2">
                <button
                  className="h-9 mt-1 px-3 py-1 bg-green-50 hover:bg-green-100 text-green-800 rounded border border-green-300 inline-flex items-center gap-1.5"
                  onClick={() => retranslateScope('book')}
                  title="Relancer la traduction du livre (toutes les langues cibles)"
                >
                  <Languages size={14} />
                  <span className="hidden sm:inline">Relancer la traduction</span>
                </button>
                <button
                  className="h-9 mt-1 px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-800 rounded border border-blue-300 inline-flex items-center gap-1.5"
                  onClick={handleDetectLanguage}
                  disabled={detectLanguageLoading}
                  title="Détecter automatiquement la langue du livre"
                >
                  <Languages size={14} />
                  <span className="hidden sm:inline">{detectLanguageLoading ? 'Détection...' : 'Détecter la langue'}</span>
                </button>
                <button
                  className="h-9 mt-1 px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded border border-gray-300"
                  onClick={() => setEditMode(true)}
                  title="Modifier le titre du livre"
                >
                  <Edit size={14} className="inline mr-1" />
                  <span className="hidden sm:inline">Modifier le titre</span>
                </button>
                <button
                  className="h-9 mt-1 px-3 py-1 bg-purple-100 hover:bg-purple-200 text-purple-800 rounded border border-purple-300 inline-flex items-center gap-1.5"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={uploadingCover}
                  title="Modifier la photo de couverture"
                >
                  <Image size={14} />
                  <span className="hidden sm:inline">{uploadingCover ? 'Upload...' : 'Modifier la couverture'}</span>
                </button>
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleCoverUpload}
                  className="hidden"
                />
              </div>
            )}
          </div>
          {/* Pourcentage de lecture */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mt-1">
            <div className="text-xs sm:text-sm text-gray-600 font-medium whitespace-nowrap">
              Progression: {Math.round(progressPct)}%
            </div>
            <div className="w-full sm:flex-1 h-1 bg-gray-200 rounded">
              <div className="h-1 bg-blue-500 rounded" style={{ width: `${Math.round(progressPct)}%` }} />
            </div>
          </div>
          {bookData.description && (
            <div className="mt-3 text-base sm:text-lg text-gray-700 leading-relaxed select-text">
              {renderContent(bookData.description)}
            </div>
          )}

          {canEditStructure && (
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              {/* Ligne 1: Mode Réorganisation et Recalculer l'ordre */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setDragDropMode(!dragDropMode)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                    dragDropMode
                      ? 'border-green-600 text-white bg-green-600 hover:bg-green-700'
                      : 'border-blue-400 text-blue-700 bg-blue-50 hover:bg-blue-100'
                  }`}
                  title="Activer/désactiver le mode réorganisation par drag & drop"
                >
                  <Move size={16} />
                  <span className="hidden sm:inline">{dragDropMode ? 'Terminer la réorganisation' : 'Mode Réorganisation'}</span>
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!bookId) return;
                    
                    const confirmed = await new Promise((resolve) => {
                      toast((toastObj) => (
                        <div className="flex flex-col gap-3">
                          <div>
                            <div className="font-semibold text-gray-900 mb-1">Recalculer l'ordre ?</div>
                            <div className="text-sm text-gray-600">Cette action va réordonner les éléments (chapitres/sections/sous-sections) selon leur position affichée.</div>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => { toast.dismiss(toastObj.id); resolve(false); }}
                              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                            >
                              Annuler
                            </button>
                            <button
                              onClick={() => { toast.dismiss(toastObj.id); resolve(true); }}
                              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                            >
                              Recalculer
                            </button>
                          </div>
                        </div>
                      ), {
                        duration: Infinity,
                        position: 'top-center',
                        style: { maxWidth: '500px', padding: '16px' },
                      });
                    });
                    
                    if (!confirmed) return;
                    
                    const loadingToast = toast.loading('Réorganisation en cours...', {
                      position: 'top-right',
                    });
                    
                    try {
                      const response = await api.post(`/books/${bookId}/chapters/reorder/`);
                      const data = response?.data || {};
                      
                      toast.success(
                        <div>
                          <div className="font-semibold">Réorganisation terminée !</div>
                          <div className="text-sm mt-1">
                            {data.chapters || 0} chapitres, {data.sections || 0} sections, {data.subsections || 0} sous-sections
                          </div>
                        </div>,
                        {
                          id: loadingToast,
                          duration: 4000,
                          position: 'top-right',
                        }
                      );
                      
                      if (typeof onBookContentChanged === 'function') {
                        await onBookContentChanged();
                      }
                    } catch (e) {
                      console.error('Erreur lors du recalcul de l\'ordre', e);
                      toast.error(
                        <div>
                          <div className="font-semibold">Erreur</div>
                          <div className="text-sm mt-1">{e?.response?.data?.error || e?.message || 'Erreur lors du recalcul'}</div>
                        </div>,
                        {
                          id: loadingToast,
                          duration: 5000,
                          position: 'top-right',
                        }
                      );
                    }
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-purple-400 text-purple-700 bg-purple-50 hover:bg-purple-100 transition-colors"
                  title="Recalculer l'ordre séquentiel de tous les chapitres, sections et sous-sections"
                >
                  <RefreshCw size={16} />
                  <span className="hidden sm:inline">Recalculer l'ordre</span>
                </button>
              </div>
              {/* Ligne 2: Ajouter un chapitre (aligné à droite sur mobile) */}
              <div className="flex justify-end sm:justify-start">
                <button
                  type="button"
                  onClick={startCreateChapter}
                  disabled={
                    Boolean(activeCreateTarget) || createStates.chapter.open
                  }
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-dashed border-blue-400 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
                >
                  + Ajouter un chapitre
                </button>
              </div>
            </div>
          )}

          {canEditStructure && createStates.chapter.open && (
            <div className="mt-4 max-w-2xl border border-gray-200 rounded-lg p-4 bg-gray-50 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-800 mb-3">Nouveau chapitre</h2>
              <label className="block text-xs font-medium text-gray-600 mb-1">Titre</label>
              <input
                type="text"
                value={createStates.chapter.title}
                onChange={(e) =>
                  setCreateStates((prev) => ({
                    ...prev,
                    chapter: { ...prev.chapter, title: e.target.value },
                  }))
                }
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3"
                placeholder="Titre du chapitre"
              />
              <div className="grid gap-1 text-xs font-medium text-gray-600">
                <span>Contenu (optionnel)</span>
                <RichTextEditor
                  content={createStates.chapter.content}
                  images={createStates.chapter.images || []}
                  tables={createStates.chapter.tables || []}
                  onChange={(content) =>
                    setCreateStates((prev) => ({
                      ...prev,
                      chapter: { ...prev.chapter, content },
                    }))
                  }
                  onImagesChange={(images) =>
                    setCreateStates((prev) => ({
                      ...prev,
                      chapter: { ...prev.chapter, images },
                    }))
                  }
                  onTablesChange={(tables) =>
                    setCreateStates((prev) => ({
                      ...prev,
                      chapter: { ...prev.chapter, tables },
                    }))
                  }
                  onUploadImage={uploadInlineImage}
                  placeholder="Résumé ou introduction du chapitre"
                  ariaLabel="Contenu du nouveau chapitre"
                  editable
                />
              </div>
              <div className="mt-3">
                <VisualTableListEditor
                  tables={createStates.chapter.tables || []}
                  onChange={(tables) =>
                    setCreateStates((prev) => ({
                      ...prev,
                      chapter: { ...prev.chapter, tables },
                    }))
                  }
                  onValidityChange={(tableIndex, isValid) =>
                    setCreateChapterTableValidity((current) => ({
                      ...current,
                      [tableIndex]: isValid,
                    }))
                  }
                  onDeleteTable={(tableId) =>
                    setCreateStates((prev) => ({
                      ...prev,
                      chapter: {
                        ...prev.chapter,
                        content: removeTableAnchor(
                          prev.chapter.content,
                          tableId
                        ),
                      },
                    }))
                  }
                />
              </div>
              {(createStates.chapter.tables || []).some(
                (_, tableIndex) =>
                  createChapterTableValidity[tableIndex] === false
              ) && (
                <p role="alert" className="mt-2 text-sm text-red-700">
                  Corrigez les largeurs de colonnes avant d’enregistrer.
                </p>
              )}
              {/* Position du chapitre */}
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Position</label>
                  <select
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    value={createStates.chapter.position}
                    onChange={(e) =>
                      setCreateStates((prev) => ({
                        ...prev,
                        chapter: { ...prev.chapter, position: e.target.value, afterId: '' },
                      }))
                    }
                  >
                    <option value="first">Au début du livre</option>
                    <option value="last">À la fin du livre</option>
                    <option value="after">Après un autre chapitre</option>
                  </select>
                </div>
                {createStates.chapter.position === 'after' && Array.isArray(bookData?.chapters) && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Après le chapitre</label>
                    <select
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      value={createStates.chapter.afterId}
                      onChange={(e) =>
                        setCreateStates((prev) => ({
                          ...prev,
                          chapter: { ...prev.chapter, afterId: e.target.value },
                        }))
                      }
                    >
                      <option value="">-- Choisir un chapitre --</option>
                      {bookData.chapters.map((ch) => (
                        <option key={ch.id} value={ch.id}>
                          {cleanTitle(ch.title, ch.order) || `Chapitre ${ch.order}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={saveCreateChapter}
                  disabled={(createStates.chapter.tables || []).some(
                    (_, tableIndex) =>
                      createChapterTableValidity[tableIndex] === false
                  )}
                  className="px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Enregistrer le chapitre
                </button>
                <button
                  type="button"
                  onClick={cancelCreateChapter}
                  className="px-3 py-1.5 text-sm font-medium bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contenu des chapitres, groupés par thématique */}
      <div className="w-full space-y-12">
        {groupedChapters.map((group) => (
          <div key={group.key} className="w-full">
            {group.thematique ? (
              <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-center gap-2 text-purple-800">
                  <span className="font-semibold text-purple-900">Thématique:</span>
                  <span className="text-purple-700">{group.thematique.title}</span>
                </div>
                {group.thematique.description && (
                  <p className="text-sm text-purple-600 mt-1 italic">
                    {group.thematique.description}
                  </p>
                )}
              </div>
            ) : null}

            <div className="w-full space-y-10">
              {group.chapters.map((chapter) => {
                const chapterIndexOriginal = displayData.chapters.findIndex(c => c.id === chapter.id);
                const isIntroChapter = (ch) => {
                  if (!ch) return false;
                  return !!ch.is_intro;
                };
                const isIntro = isIntroChapter(chapter);
                const nonIntroChaptersBefore = displayData?.chapters?.filter(c => (c.order < chapter.order) && !isIntroChapter(c)).length || 0;
                const displayChapterNumber = nonIntroChaptersBefore + 1;
                return (
                  <div
                    key={chapter.id}
                    data-hierarchy-item={`chapter:${chapter.id}`}
                    className={`w-full ${dragDropMode ? 'border-2 border-dashed border-blue-300 rounded-lg p-4 bg-blue-50/30 hover:bg-blue-50/50 transition-colors' : ''}`}
                    draggable={dragDropMode}
                    onDragStart={(e) => handleDragStart(e, 'chapter', chapter.id)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDropChapter(e, chapter.id)}
                  >
                    {/* Titre du chapitre (H1) + actions admin */}
                    <div className="mb-6">
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2 flex-1">
                          {dragDropMode && (
                            <>
                              <div className="flex flex-col mr-1">
                                <button
                                  type="button"
                                  onClick={() => moveChapterUp(chapter.id)}
                                  disabled={chapterIndexOriginal === 0}
                                  className={`p-0.5 rounded ${chapterIndexOriginal === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100'}`}
                                  title="Monter le chapitre"
                                >
                                  <ChevronUp size={18} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveChapterDown(chapter.id)}
                                  disabled={chapterIndexOriginal === (displayData?.chapters?.length || 0) - 1}
                                  className={`p-0.5 rounded ${chapterIndexOriginal === (displayData?.chapters?.length || 0) - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100'}`}
                                  title="Descendre le chapitre"
                                >
                                  <ChevronDown size={18} />
                                </button>
                              </div>
                              <GripVertical 
                                size={24} 
                                className="text-blue-600 cursor-move flex-shrink-0" 
                                title="Glisser pour déplacer"
                              />
                            </>
                          )}
                          <h1 
                            id={`chapter-${chapter.id}`}
                            className={`text-3xl font-bold text-gray-900 ${selectedItem?.type === 'chapter' && selectedItem?.chapterIndex === chapterIndexOriginal ? 'bg-blue-50 border-l-4 border-blue-500 pl-4 py-2 -ml-6' : ''}`}
                          >
                            {isIntro
                              ? cleanTitle(editStates.chapters[chapter.id]?.titleOverride || chapter.title, chapter.order)
                              : <>Chapitre {displayChapterNumber}. {cleanTitle(editStates.chapters[chapter.id]?.titleOverride || chapter.title, chapter.order)}</>}
                          </h1>
                        </div>
                        {/* Desktop: Boutons à côté du titre */}
                        <div className="hidden sm:flex items-center gap-2 justify-end">
                        {(isAdmin && onRegenerateChapter) && (
                          <>
                            <select
                              value={qcmLanguages[chapter.id] || bookData?.book?.language || bookData?.language || 'fr'}
                              onChange={(e) => setQcmLanguages(prev => ({ ...prev, [chapter.id]: e.target.value }))}
                              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              title="Langue du QCM"
                            >
                              <option value="fr">🇫🇷 Français</option>
                              <option value="en">🇬🇧 English</option>
                              <option value="pt">🇵🇹 Português</option>
                            </select>
                            <button
                              type="button"
                              onClick={() => onRegenerateChapter(chapter.id, qcmLanguages[chapter.id] || bookData?.book?.language || bookData?.language || 'fr')}
                              disabled={regenLoading}
                              className={`inline-flex items-center gap-1.5 border-none rounded-md px-3 py-2 cursor-pointer transition-all duration-300 text-sm ${regenLoading ? 'bg-gray-300 text-gray-600' : 'bg-warning text-white hover:bg-warning hover:-translate-y-0.5'}`}
                              title="Régénérer le QCM (5 questions)"
                            >
                              <RefreshCw size={16} />
                              Régénérer QCM
                            </button>
                          </>
                        )}
                        {(canEditStructure) && (
                          <>
                            <button
                              type="button"
                              onClick={() => retranslateScope('chapter', { chapter_id: chapter.id })}
                              className="inline-flex items-center gap-1.5 border border-green-300 text-green-800 bg-green-50 rounded-md px-3 py-2 text-sm hover:bg-green-100"
                              title="Relancer la traduction de ce chapitre"
                            >
                              <Languages size={16} />
                              Retraduire
                            </button>
                            <button
                              type="button"
                              onClick={() => startEdit('chapters', { id: chapter.id }, { title: chapter.title, content: chapter.content, is_intro: chapter.is_intro, images: chapter.images, videos: chapter.videos, tables: chapter.tables, image_display_config: chapter.image_display_config, video_display_config: chapter.video_display_config })}
                              className="inline-flex items-center gap-1.5 border border-gray-300 text-gray-800 rounded-md px-3 py-2 text-sm hover:bg-gray-50"
                            >
                              <Edit size={16} />
                              Modifier
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteChapter(chapter.id)}
                              className="inline-flex items-center gap-1.5 border border-red-300 text-red-700 bg-red-50 rounded-md px-3 py-2 text-sm hover:bg-red-100"
                              title="Supprimer ce chapitre"
                            >
                              <Trash2 size={16} />
                              Supprimer
                            </button>
                          </>
                        )}
                        </div>
                      </div>
                      {/* Mobile: Boutons sur nouvelle ligne avec icônes seules */}
                      <div className="sm:hidden flex items-center gap-2 mt-2 justify-end">
                        {(isAdmin && onRegenerateChapter) && (
                          <>
                            <select
                              value={qcmLanguages[chapter.id] || bookData?.book?.language || bookData?.language || 'fr'}
                              onChange={(e) => setQcmLanguages(prev => ({ ...prev, [chapter.id]: e.target.value }))}
                              className="border border-gray-300 rounded-md px-1.5 py-1 text-xs bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              title="Langue du QCM"
                            >
                              <option value="fr">fren</option>
                              <option value="en">eng</option>
                              <option value="pt">pt</option>
                            </select>
                            <button
                              type="button"
                              onClick={() => onRegenerateChapter(chapter.id, qcmLanguages[chapter.id] || bookData?.book?.language || bookData?.language || 'fr')}
                              disabled={regenLoading}
                              className={`inline-flex items-center gap-1 border-none rounded-md px-2 py-1 cursor-pointer transition-all duration-300 text-xs ${regenLoading ? 'bg-gray-300 text-gray-600' : 'bg-warning text-white hover:bg-warning'}`}
                              title="Régénérer le QCM"
                            >
                              <RefreshCw size={14} />
                              <span>QCM</span>
                            </button>
                          </>
                        )}
                        {(canEditStructure) && (
                          <>
                            <button
                              type="button"
                              onClick={() => retranslateScope('chapter', { chapter_id: chapter.id })}
                              className="inline-flex items-center justify-center border border-green-300 text-green-800 bg-green-50 rounded-md p-1.5 text-sm hover:bg-green-100"
                              title="Relancer la traduction de ce chapitre"
                            >
                              <Languages size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => startEdit('chapters', { id: chapter.id }, { title: chapter.title, content: chapter.content, is_intro: chapter.is_intro, images: chapter.images, videos: chapter.videos, tables: chapter.tables, image_display_config: chapter.image_display_config, video_display_config: chapter.video_display_config })}
                              className="inline-flex items-center justify-center border border-gray-300 text-gray-800 rounded-md p-1.5 text-sm hover:bg-gray-50"
                              title="Modifier"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteChapter(chapter.id)}
                              className="inline-flex items-center justify-center border border-red-300 text-red-700 bg-red-50 rounded-md p-1.5 text-sm hover:bg-red-100"
                              title="Supprimer"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {renderContextualCreation(
                      {
                        currentType: 'chapter',
                        currentItem: chapter,
                        chapter,
                        section: null,
                        subsectionAncestors: [],
                      },
                      String(displayChapterNumber)
                    )}

                    {/* Contenu du chapitre */}
                    {editStates.chapters[chapter.id]?.editing ? (
                      <div className="mb-6 border border-gray-200 rounded p-3 bg-gray-50">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Titre du chapitre</label>
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded p-2 text-sm mb-3"
                          value={
                            typeof editStates.chapters[chapter.id]?.title === 'string'
                              ? editStates.chapters[chapter.id].title
                              : (chapter.title || '')
                          }
                          onChange={(e) => onChangeEditField('chapters', chapter.id, 'title', e.target.value)}
                        />
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contenu (texte)</label>
                        <RichTextEditor
                          content={editStates.chapters[chapter.id]?.content || ''}
                          onChange={(value) => onChangeEditField('chapters', chapter.id, 'content', value)}
                          images={parseJsonArray(editStates.chapters[chapter.id]?.imagesText)}
                          tables={parseJsonArray(editStates.chapters[chapter.id]?.tablesText)}
                          onImagesChange={(images) =>
                            onChangeEditField(
                              'chapters',
                              chapter.id,
                              'imagesText',
                              JSON.stringify(images, null, 2)
                            )
                          }
                          onTablesChange={(tables) =>
                            onChangeEditField(
                              'chapters',
                              chapter.id,
                              'tablesText',
                              JSON.stringify(tables, null, 2)
                            )
                          }
                          onUploadImage={uploadInlineImage}
                          contentLanguage={contentLanguage}
                          canManageImages={canEditStructure}
                          canManageImageStructure={canManageImageStructure}
                          placeholder="Écrivez le contenu de votre chapitre ici..."
                          editable={true}
                          className="mb-3"
                        />
                        <div className="mb-3 space-y-3">
                          <h3 className="text-sm font-semibold text-gray-800">
                            Propositions des images de galerie
                          </h3>
                          <GalleryImageProposalsEditor
                            images={parseJsonArray(
                              editStates.chapters[chapter.id]?.imagesText
                            )}
                            onChange={(images) =>
                              updateDraftImages('chapters', chapter.id, images)
                            }
                            onUploadImage={uploadInlineImage}
                            canEdit={canEditStructure}
                            canManageStructure={canManageImageStructure}
                          />
                        </div>

                        <div className="mt-3 flex items-center gap-2">
                          <input
                            id={`chapter-${chapter.id}-is-intro`}
                            type="checkbox"
                            className="h-4 w-4"
                            checked={!!editStates.chapters[chapter.id]?.is_intro}
                            onChange={(e) => onChangeEditField('chapters', chapter.id, 'is_intro', e.target.checked)}
                          />
                          <label htmlFor={`chapter-${chapter.id}-is-intro`} className="text-sm text-gray-700">
                            Chapitre d'introduction (sans numéro ni icône de dossier)
                          </label>
                        </div>
                        {/* Configuration d'affichage des images */}
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Configuration d'affichage des images</label>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Taille des images</label>
                              <select
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                value={editStates.chapters[chapter.id]?.image_display_config?.size || chapter.image_display_config?.size || 'medium'}
                                onChange={(e) => {
                                  const currentConfig = editStates.chapters[chapter.id]?.image_display_config || chapter.image_display_config || {};
                                  onChangeEditField('chapters', chapter.id, 'image_display_config', { ...currentConfig, size: e.target.value });
                                }}
                              >
                                <option value="small">Petite</option>
                                <option value="medium">Moyenne</option>
                                <option value="large">Grande</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Nombre de colonnes</label>
                              <select
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                value={editStates.chapters[chapter.id]?.image_display_config?.columns || chapter.image_display_config?.columns || 2}
                                onChange={(e) => {
                                  const currentConfig = editStates.chapters[chapter.id]?.image_display_config || chapter.image_display_config || {};
                                  onChangeEditField('chapters', chapter.id, 'image_display_config', { ...currentConfig, columns: parseInt(e.target.value) });
                                }}
                              >
                                <option value="1">1 par ligne</option>
                                <option value="2">2 par ligne</option>
                                <option value="3">3 par ligne</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Vidéos du chapitre: upload */}
                        <div className="mt-3 flex flex-col gap-1 mr-4">
                          <label className="text-xs font-medium text-gray-700">Vidéos</label>
                          <input
                            type="file"
                            accept="video/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const form = new FormData();
                              form.append('file', file);
                              try {
                                const res = await api.post('/uploads/videos/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
                                const payload = res?.data;
                                const arr = (() => { try { return JSON.parse(editStates.chapters[chapter.id]?.videosText || '[]'); } catch { return []; } })();
                                arr.push(payload);
                                onChangeEditField('chapters', chapter.id, 'videosText', JSON.stringify(arr, null, 2));
                              } catch (err) {
                                console.error('Upload vidéo chapitre échoué', err);
                              } finally {
                                e.target.value = '';
                              }
                            }}
                          />
                          <span className="text-xs text-gray-500">Uploader et ajouter automatiquement à la liste</span>
                        </div>

                        {/* Configuration d'affichage des vidéos */}
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Configuration d'affichage des vidéos</label>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Taille des vidéos</label>
                              <select
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                value={editStates.chapters[chapter.id]?.video_display_config?.size || chapter.video_display_config?.size || 'medium'}
                                onChange={(e) => {
                                  const currentConfig = editStates.chapters[chapter.id]?.video_display_config || chapter.video_display_config || {};
                                  onChangeEditField('chapters', chapter.id, 'video_display_config', { ...currentConfig, size: e.target.value });
                                }}
                              >
                                <option value="small">Petite</option>
                                <option value="medium">Moyenne</option>
                                <option value="large">Grande</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Nombre de colonnes</label>
                              <select
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                value={editStates.chapters[chapter.id]?.video_display_config?.columns || chapter.video_display_config?.columns || 2}
                                onChange={(e) => {
                                  const currentConfig = editStates.chapters[chapter.id]?.video_display_config || chapter.video_display_config || {};
                                  onChangeEditField('chapters', chapter.id, 'video_display_config', { ...currentConfig, columns: parseInt(e.target.value) });
                                }}
                              >
                                <option value="1">1 par ligne</option>
                                <option value="2">2 par ligne</option>
                                <option value="3">3 par ligne</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Liste des vidéos */}
                        {(() => {
                          let arr = [];
                          try { arr = JSON.parse(editStates.chapters[chapter.id]?.videosText || '[]'); } catch {}
                          if (!Array.isArray(arr) || arr.length === 0) return null;
                          return (
                            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                              {arr.map((vid, idx) => {
                                const url = typeof vid === 'string' ? vid : vid?.url;
                                const caption = typeof vid === 'object' ? vid?.caption : undefined;
                                const fullUrl = buildImageUrl(url || '');
                                return (
                                  <div key={idx} className="border rounded p-2 flex flex-col gap-2 bg-white">
                                    {fullUrl ? (
                                      <video src={fullUrl} controls className="w-full h-40 bg-black rounded" />
                                    ) : (
                                      <div className="text-xs text-gray-500">Entrée de vidéo invalide</div>
                                    )}
                                    <input
                                      type="text"
                                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                                      placeholder="Titre / légende de la vidéo"
                                      value={caption || ''}
                                      onChange={(e) => {
                                        const next = arr.map((item, i) => {
                                          if (i !== idx) return item;
                                          if (typeof item === 'string') {
                                            return { url: item, caption: e.target.value };
                                          }
                                          return { ...item, caption: e.target.value };
                                        });
                                        onChangeEditField('chapters', chapter.id, 'videosText', JSON.stringify(next, null, 2));
                                      }}
                                    />
                                    <button
                                      type="button"
                                      className="self-start px-2 py-1 text-xs bg-red-600 text-white rounded"
                                      onClick={() => {
                                        const next = arr.filter((_, i) => i !== idx);
                                        onChangeEditField('chapters', chapter.id, 'videosText', JSON.stringify(next, null, 2));
                                      }}
                                    >Supprimer</button>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}

                        {/* Tableaux du chapitre: builder lignes/colonnes + grille */}
                        {(() => {
                          let tbls = [];
                          try { tbls = JSON.parse(editStates.chapters[chapter.id]?.tablesText || '[]'); } catch {}
                          if (!Array.isArray(tbls)) tbls = [];

                          const ensureShape = (t) => {
                            const clone = { ...(t || {}) };
                            let rows = Number.isInteger(clone.rows) ? clone.rows : null;
                            let columns = Number.isInteger(clone.columns) ? clone.columns : null;
                            let grid = Array.isArray(clone.grid) ? clone.grid.map((r) => Array.isArray(r) ? [...r] : []) : null;

                            if (!rows || !columns || !grid || grid.length === 0) {
                              const c = clone.content;
                              let rawLines = [];
                              if (typeof c === 'string') {
                                rawLines = c.split('\n');
                              } else if (Array.isArray(c)) {
                                rawLines = c.flatMap((item) => (typeof item === 'string' ? item.split('\n') : []));
                              }
                              let lines = rawLines.map((l) => (l ?? '').trim()).filter((l) => l.length > 0);
                              const hasPipe = lines.some((l) => l.includes('|'));
                              if (hasPipe) {
                                lines = lines.filter((l) => l.includes('|'));
                              }
                              const parsedRows = lines.map((l) => l.split('|').map((cell) => cell.trim()));
                              const colCount = parsedRows.length > 0 ? Math.max(...parsedRows.map((r) => r.length)) : 2;
                              const rowCount = parsedRows.length > 0 ? parsedRows.length : 2;
                              rows = rowCount;
                              columns = colCount;
                              grid = Array.from({ length: rows }, (_, ri) =>
                                Array.from({ length: columns }, (_, ci) => parsedRows[ri]?.[ci] ?? '')
                              );
                            }

                            if (grid.length !== rows) {
                              grid = Array.from({ length: rows }, (_, ri) => grid[ri] || []);
                            }
                            grid = grid.map((row) => {
                              if (!Array.isArray(row)) return Array.from({ length: columns }, () => '');
                              if (row.length < columns) {
                                return [...row, ...Array.from({ length: columns - row.length }, () => '')];
                              }
                              return row.slice(0, columns);
                            });

                            const content = grid.map((r) => r.join(' | ')).join('\n');
                            return { ...clone, rows, columns, grid, content };
                          };

                          const update = (updater) => {
                            const normalized = tbls.map((t) => ensureShape(t));
                            const next = updater(normalized).map((t) => {
                              const withShape = ensureShape(t);
                              return {
                                ...withShape,
                                content: withShape.grid.map((r) => r.join(' | ')).join('\n'),
                              };
                            });
                            onChangeEditField('chapters', chapter.id, 'tablesText', JSON.stringify(next, null, 2));
                          };

                          return (
                            <div className="mt-3 space-y-3">
                              {tbls.map((t, idx) => {
                                const shaped = ensureShape(t);
                                return (
                                  <div key={idx} className="border rounded p-2 bg-white">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                                      <div className="md:col-span-1">
                                        <label className="text-xs text-gray-600">Intitulé du tableau</label>
                                        <input
                                          className="w-full border border-gray-300 rounded p-1 text-sm"
                                          value={shaped.title || ''}
                                          onChange={(e) =>
                                            update((arr) => {
                                              arr[idx] = { ...shaped, title: e.target.value };
                                              return arr;
                                            })
                                          }
                                        />
                                      </div>
                                      <div>
                                        <label className="text-xs text-gray-600">Colonnes</label>
                                        <input
                                          type="number"
                                          min={1}
                                          max={12}
                                          className="w-full border border-gray-300 rounded p-1 text-sm"
                                          value={shaped.columns || 1}
                                          onChange={(e) => {
                                            const nextCols = Math.max(1, Math.min(12, parseInt(e.target.value || '1', 10)));
                                            update((arr) => {
                                              const current = ensureShape(arr[idx] || shaped);
                                              const newGrid = current.grid.map((row) => {
                                                if (row.length < nextCols) {
                                                  return [...row, ...Array.from({ length: nextCols - row.length }, () => '')];
                                                }
                                                return row.slice(0, nextCols);
                                              });
                                              arr[idx] = normalizeTableShape(
                                                { ...current, grid: newGrid },
                                                nextCols
                                              );
                                              return arr;
                                            });
                                          }}
                                        />
                                      </div>
                                      <div>
                                        <label className="text-xs text-gray-600">Lignes</label>
                                        <input
                                          type="number"
                                          min={1}
                                          max={30}
                                          className="w-full border border-gray-300 rounded p-1 text-sm"
                                          value={shaped.rows || 1}
                                          onChange={(e) => {
                                            const nextRows = Math.max(1, Math.min(30, parseInt(e.target.value || '1', 10)));
                                            update((arr) => {
                                              const current = ensureShape(arr[idx] || shaped);
                                              let newGrid = current.grid;
                                              if (newGrid.length < nextRows) {
                                                newGrid = [
                                                  ...newGrid,
                                                  ...Array.from({ length: nextRows - newGrid.length }, () =>
                                                    Array.from({ length: current.columns || 1 }, () => '')
                                                  ),
                                                ];
                                              } else if (newGrid.length > nextRows) {
                                                newGrid = newGrid.slice(0, nextRows);
                                              }
                                              arr[idx] = { ...current, rows: nextRows, grid: newGrid };
                                              return arr;
                                            });
                                          }}
                                        />
                                      </div>
                                    </div>

                                    <div className="overflow-x-auto">
                                      <TableEditor
                                        table={shaped}
                                        onValidityChange={(isValid) =>
                                          updateTableValidity(
                                            'chapters',
                                            chapter.id,
                                            idx,
                                            isValid
                                          )
                                        }
                                        onChange={(nextTable) =>
                                          update((arr) => {
                                            arr[idx] = nextTable;
                                            return arr;
                                          })
                                        }
                                      />
                                      <table className="min-w-full border border-gray-200 text-xs">
                                        <tbody>
                                          {shaped.grid.map((row, ri) => (
                                            <tr key={ri} className={ri === 0 ? 'bg-gray-50' : ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                              {row.map((cell, ci) => (
                                                <td key={ci} className="border border-gray-200 p-1 align-top">
                                                  <input
                                                    className="w-full border border-gray-200 rounded px-1 py-0.5 text-[11px] bg-white"
                                                    value={cell}
                                                    onChange={(e) =>
                                                      update((arr) => {
                                                        const current = ensureShape(arr[idx] || shaped);
                                                        const g = current.grid.map((r) => [...r]);
                                                        g[ri][ci] = e.target.value;
                                                        arr[idx] = { ...current, grid: g };
                                                        return arr;
                                                      })
                                                    }
                                                  />
                                                </td>
                                              ))}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>

                                    <div className="mt-2 flex items-center gap-2">
                                      <button
                                        type="button"
                                        className="px-2 py-1 text-xs bg-red-600 text-white rounded"
                                        onClick={() => update((arr) => arr.filter((_, i) => i !== idx))}
                                      >
                                        Supprimer
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                              <button
                                type="button"
                                className="px-3 py-1 text-sm bg-gray-100 border rounded"
                                onClick={() =>
                                  update((arr) => [
                                    ...arr,
                                    {
                                      title: '',
                                      rows: 2,
                                      columns: 2,
                                      grid: [
                                        ['', ''],
                                        ['', ''],
                                      ],
                                      content: '|',
                                    },
                                  ])
                                }
                              >
                                Ajouter un tableau
                              </button>
                            </div>
                          );
                        })()}
                        {hasInvalidTableLayout(
                          'chapters',
                          chapter.id,
                          parseJsonArray(
                            editStates.chapters[chapter.id]?.tablesText
                          )
                        ) && (
                          <p role="alert" className="mt-2 text-sm text-red-700">
                            Corrigez les largeurs de colonnes avant
                            d’enregistrer.
                          </p>
                        )}
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            className="px-3 py-1 bg-blue-600 text-white rounded text-sm"
                            disabled={hasInvalidTableLayout(
                              'chapters',
                              chapter.id,
                              parseJsonArray(
                                editStates.chapters[chapter.id]?.tablesText
                              )
                            )}
                            onClick={() => saveChapter(chapter)}
                          >
                            Enregistrer
                          </button>
                          <button
                            className="px-3 py-1 bg-gray-200 text-gray-800 rounded text-sm"
                            onClick={() => cancelEdit('chapters', chapter.id)}
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    ) : (
                      (editStates.chapters[chapter.id]?.contentOverride || chapter.content) && (
                        <div className="text-gray-800 leading-relaxed mb-6 space-y-4 select-text [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6">
                          {renderRichOrLegacyContent(
                            editStates.chapters[chapter.id]?.contentOverride || chapter.content,
                            editStates.chapters[chapter.id]?.imagesOverride ?? chapter.images,
                            editStates.chapters[chapter.id]?.tablesOverride ?? chapter.tables
                          )}
                        </div>
                      )
                    )}

                    {/* Images du chapitre */}
                    {renderImages(
                      editStates.chapters[chapter.id]?.imagesOverride ?? chapter.images,
                      editStates.chapters[chapter.id]?.image_display_config || chapter.image_display_config
                    )}

                    {/* Vidéos du chapitre */}
                    {renderVideos(
                      editStates.chapters[chapter.id]?.videosOverride ?? chapter.videos,
                      editStates.chapters[chapter.id]?.video_display_config || chapter.video_display_config || editStates.chapters[chapter.id]?.image_display_config || chapter.image_display_config
                    )}

                    {/* Tableaux du chapitre */}
                    {renderTables(
                      editStates.chapters[chapter.id]?.tablesOverride ?? chapter.tables,
                      editStates.chapters[chapter.id]?.contentOverride || chapter.content
                    )}

                    {/* Sections du chapitre */}
                    <div className="min-w-0 max-w-full space-y-6 pl-4 pr-2 sm:pr-4">
                      {chapter.sections.map((section, sectionIndex) => (
                        <div 
                          key={section.id} 
                          data-hierarchy-item={`section:${section.id}`}
                          className={`w-full ${dragDropMode ? 'border-2 border-dashed border-green-300 rounded-lg p-3 bg-green-50/30 hover:bg-green-50/50 transition-colors my-2' : ''}`}
                          draggable={dragDropMode}
                          onDragStart={(e) => handleDragStart(e, 'section', section.id, chapter.id)}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDropSection(e, chapter.id, section.id)}
                        >
                          {/* Titre de la section (H2) */}
                          <div className="mb-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-2 flex-1">
                                {dragDropMode && (
                                  <GripVertical 
                                    size={20} 
                                    className="text-green-600 cursor-move flex-shrink-0" 
                                    title="Glisser pour déplacer"
                                  />
                                )}
                                <h2 
                                id={`section-${section.id}`}
                                className={`text-2xl font-semibold text-gray-800 ${selectedItem?.type === 'section' && selectedItem?.chapterIndex === chapterIndexOriginal && selectedItem?.sectionIndex === sectionIndex ? 'bg-green-50 border-l-4 border-green-500 pl-4 py-2 -ml-4' : ''}`}
                                >
                                  {isIntro
                                    ? `${section.order + 1} ${cleanTitle(editStates.sections[section.id]?.titleOverride || section.title, section.order)}`
                                    : `${displayChapterNumber}.${section.order + 1} ${cleanTitle(editStates.sections[section.id]?.titleOverride || section.title, section.order)}`}
                                </h2>
                              </div>
                              {/* Desktop: Boutons à côté du titre */}
                              {canEditStructure && (
                                <div className="hidden sm:flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => retranslateScope('section', { section_id: section.id })}
                                    className="inline-flex items-center gap-1.5 mt-1 border border-green-300 text-green-800 bg-green-50 rounded-md px-3 py-1 text-sm hover:bg-green-100"
                                    title="Relancer la traduction de cette section"
                                  >
                                    <Languages size={14} />
                                    Retraduire
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => startEdit('sections', { id: section.id }, { title: section.title, content: section.content, images: section.images, videos: section.videos, tables: section.tables, image_display_config: section.image_display_config, video_display_config: section.video_display_config })}
                                    className="inline-flex items-center gap-1.5 mt-1 border border-gray-300 text-gray-800 rounded-md px-3 py-1 text-sm hover:bg-gray-50"
                                  >
                                    <Edit size={14} />
                                    Modifier
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => deleteSection(chapter.id, section.id)}
                                    className="inline-flex items-center gap-1.5 mt-1 border border-red-300 text-red-700 bg-red-50 rounded-md px-3 py-1 text-sm hover:bg-red-100"
                                    title="Supprimer cette section"
                                  >
                                    <Trash2 size={14} />
                                    Supprimer
                                  </button>
                                </div>
                              )}
                            </div>
                            {/* Mobile: Boutons sur nouvelle ligne avec icônes seules */}
                            {canEditStructure && (
                              <div className="sm:hidden flex items-center gap-2 mt-2 justify-end">
                                <button
                                  type="button"
                                  onClick={() => startEdit('sections', { id: section.id }, { title: section.title, content: section.content, images: section.images, videos: section.videos, tables: section.tables, image_display_config: section.image_display_config, video_display_config: section.video_display_config })}
                                  className="inline-flex items-center justify-center border border-gray-300 text-gray-800 rounded-md p-1.5 text-sm hover:bg-gray-50"
                                  title="Modifier"
                                >
                                  <Edit size={14} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteSection(chapter.id, section.id)}
                                  className="inline-flex items-center justify-center border border-red-300 text-red-700 bg-red-50 rounded-md p-1.5 text-sm hover:bg-red-100"
                                  title="Supprimer"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            )}
                          </div>

                          {renderContextualCreation(
                            {
                              currentType: 'section',
                              currentItem: section,
                              chapter,
                              section,
                              subsectionAncestors: [],
                            },
                            isIntro
                              ? String(section.order + 1)
                              : `${displayChapterNumber}.${section.order + 1}`
                          )}

                          {/* Contenu de la section */}
                          {editStates.sections[section.id]?.editing ? (
                            <div className="mb-4 border border-gray-200 rounded p-3 bg-gray-50">
                              <label className="block text-sm font-medium text-gray-700 mb-1">Titre de la section</label>
                              <input
                                type="text"
                                className="w-full border border-gray-300 rounded p-2 text-sm mb-2"
                                value={
                                  typeof editStates.sections[section.id]?.title === 'string'
                                    ? editStates.sections[section.id].title
                                    : (section.title || '')
                                }
                                onChange={(e) => onChangeEditField('sections', section.id, 'title', e.target.value)}
                              />
                              <label className="block text-sm font-medium text-gray-700 mb-1">Contenu (texte)</label>
                              <RichTextEditor
                                content={editStates.sections[section.id]?.content || ''}
                                onChange={(value) => onChangeEditField('sections', section.id, 'content', value)}
                                images={parseJsonArray(editStates.sections[section.id]?.imagesText)}
                                tables={parseJsonArray(editStates.sections[section.id]?.tablesText)}
                                onImagesChange={(images) =>
                                  onChangeEditField(
                                    'sections',
                                    section.id,
                                    'imagesText',
                                    JSON.stringify(images, null, 2)
                                  )
                                }
                                onTablesChange={(tables) =>
                                  onChangeEditField(
                                    'sections',
                                    section.id,
                                    'tablesText',
                                    JSON.stringify(tables, null, 2)
                                  )
                                }
                                onUploadImage={uploadInlineImage}
                                contentLanguage={contentLanguage}
                                canManageImages={canEditStructure}
                                canManageImageStructure={canManageImageStructure}
                                placeholder="Écrivez le contenu de votre section ici..."
                                editable={true}
                                className="mb-2"
                              />
                              <div className="mb-3 space-y-3">
                                <h3 className="text-sm font-semibold text-gray-800">
                                  Propositions des images de galerie
                                </h3>
                                <GalleryImageProposalsEditor
                                  images={parseJsonArray(
                                    editStates.sections[section.id]?.imagesText
                                  )}
                                  onChange={(images) =>
                                    updateDraftImages(
                                      'sections',
                                      section.id,
                                      images
                                    )
                                  }
                                  onUploadImage={uploadInlineImage}
                                  canEdit={canEditStructure}
                                  canManageStructure={canManageImageStructure}
                                />
                              </div>
                              {/* Configuration d'affichage des images */}
                              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Configuration d'affichage des images</label>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">Taille des images</label>
                                    <select
                                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                      value={editStates.sections[section.id]?.image_display_config?.size || section.image_display_config?.size || 'medium'}
                                      onChange={(e) => {
                                        const currentConfig = editStates.sections[section.id]?.image_display_config || section.image_display_config || {};
                                        onChangeEditField('sections', section.id, 'image_display_config', { ...currentConfig, size: e.target.value });
                                      }}
                                    >
                                      <option value="small">Petite</option>
                                      <option value="medium">Moyenne</option>
                                      <option value="large">Grande</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">Nombre de colonnes</label>
                                    <select
                                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                      value={editStates.sections[section.id]?.image_display_config?.columns || section.image_display_config?.columns || 2}
                                      onChange={(e) => {
                                        const currentConfig = editStates.sections[section.id]?.image_display_config || section.image_display_config || {};
                                        onChangeEditField('sections', section.id, 'image_display_config', { ...currentConfig, columns: parseInt(e.target.value) });
                                      }}
                                    >
                                      <option value="1">1 par ligne</option>
                                      <option value="2">2 par ligne</option>
                                      <option value="3">3 par ligne</option>
                                    </select>
                                  </div>
                                </div>
                              </div>

                              {/* Vidéos: upload */}
                              <div className="mt-3 flex flex-col gap-1 mr-4">
                                <label className="text-xs font-medium text-gray-700">Vidéos</label>
                                <input
                                  type="file"
                                  accept="video/*"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    const form = new FormData();
                                    form.append('file', file);
                                    try {
                                      const res = await api.post('/uploads/videos/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
                                      const payload = res?.data;
                                      const arr = (() => { try { return JSON.parse(editStates.sections[section.id]?.videosText || '[]'); } catch { return []; } })();
                                      arr.push(payload);
                                      onChangeEditField('sections', section.id, 'videosText', JSON.stringify(arr, null, 2));
                                    } catch (err) {
                                      console.error('Upload vidéo section échoué', err);
                                    } finally {
                                      e.target.value = '';
                                    }
                                  }}
                                />
                                <span className="text-xs text-gray-500">Uploader et ajouter automatiquement à la liste</span>
                              </div>

                              {/* Configuration d'affichage des vidéos */}
                              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Configuration d'affichage des vidéos</label>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">Taille des vidéos</label>
                                    <select
                                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                      value={editStates.sections[section.id]?.video_display_config?.size || section.video_display_config?.size || 'medium'}
                                      onChange={(e) => {
                                        const currentConfig = editStates.sections[section.id]?.video_display_config || section.video_display_config || {};
                                        onChangeEditField('sections', section.id, 'video_display_config', { ...currentConfig, size: e.target.value });
                                      }}
                                    >
                                      <option value="small">Petite</option>
                                      <option value="medium">Moyenne</option>
                                      <option value="large">Grande</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">Nombre de colonnes</label>
                                    <select
                                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                      value={editStates.sections[section.id]?.video_display_config?.columns || section.video_display_config?.columns || 2}
                                      onChange={(e) => {
                                        const currentConfig = editStates.sections[section.id]?.video_display_config || section.video_display_config || {};
                                        onChangeEditField('sections', section.id, 'video_display_config', { ...currentConfig, columns: parseInt(e.target.value) });
                                      }}
                                    >
                                      <option value="1">1 par ligne</option>
                                      <option value="2">2 par ligne</option>
                                      <option value="3">3 par ligne</option>
                                    </select>
                                  </div>
                                </div>
                              </div>

                              {/* Liste des vidéos */}
                              {(() => {
                                let arr = [];
                                try { arr = JSON.parse(editStates.sections[section.id]?.videosText || '[]'); } catch {}
                                if (!Array.isArray(arr) || arr.length === 0) return null;
                                return (
                                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {arr.map((vid, idx) => {
                                      const url = typeof vid === 'string' ? vid : vid?.url;
                                      const caption = typeof vid === 'object' ? vid?.caption : undefined;
                                      const fullUrl = buildImageUrl(url || '');
                                      return (
                                        <div key={idx} className="border rounded p-2 flex flex-col gap-2 bg-white">
                                          {fullUrl ? (
                                            <video src={fullUrl} controls className="w-full h-40 bg-black rounded" />
                                          ) : (
                                            <div className="text-xs text-gray-500">Entrée de vidéo invalide</div>
                                          )}
                                          <input
                                            type="text"
                                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                                            placeholder="Titre / légende de la vidéo"
                                            value={caption || ''}
                                            onChange={(e) => {
                                              const next = arr.map((item, i) => {
                                                if (i !== idx) return item;
                                                if (typeof item === 'string') {
                                                  return { url: item, caption: e.target.value };
                                                }
                                                return { ...item, caption: e.target.value };
                                              });
                                              onChangeEditField('sections', section.id, 'videosText', JSON.stringify(next, null, 2));
                                            }}
                                          />
                                          <button
                                            type="button"
                                            className="self-start px-2 py-1 text-xs bg-red-600 text-white rounded"
                                            onClick={() => {
                                              const next = arr.filter((_, i) => i !== idx);
                                              onChangeEditField('sections', section.id, 'videosText', JSON.stringify(next, null, 2));
                                            }}
                                          >Supprimer</button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })()}
                              {/* Tableaux: builder avec lignes/colonnes + grille (JSON caché) */}
                              {(() => {
                                let tbls = [];
                                try { tbls = JSON.parse(editStates.sections[section.id]?.tablesText || '[]'); } catch {}
                                if (!Array.isArray(tbls)) tbls = [];

                                const ensureShape = (t) => {
                                  const clone = { ...(t || {}) };
                                  let rows = Number.isInteger(clone.rows) ? clone.rows : null;
                                  let columns = Number.isInteger(clone.columns) ? clone.columns : null;
                                  let grid = Array.isArray(clone.grid) ? clone.grid.map((r) => Array.isArray(r) ? [...r] : []) : null;

                                  if (!rows || !columns || !grid || grid.length === 0) {
                                    // Essayer de déduire depuis content texte
                                    const c = clone.content;
                                    let rawLines = [];
                                    if (typeof c === 'string') {
                                      rawLines = c.split('\n');
                                    } else if (Array.isArray(c)) {
                                      rawLines = c.flatMap((item) => (typeof item === 'string' ? item.split('\n') : []));
                                    }
                                    let lines = rawLines.map((l) => (l ?? '').trim()).filter((l) => l.length > 0);
                                    const hasPipe = lines.some((l) => l.includes('|'));
                                    if (hasPipe) {
                                      lines = lines.filter((l) => l.includes('|'));
                                    }
                                    const parsedRows = lines.map((l) => l.split('|').map((cell) => cell.trim()));
                                    const colCount = parsedRows.length > 0 ? Math.max(...parsedRows.map((r) => r.length)) : 2;
                                    const rowCount = parsedRows.length > 0 ? parsedRows.length : 2;
                                    rows = rowCount;
                                    columns = colCount;
                                    grid = Array.from({ length: rows }, (_, ri) =>
                                      Array.from({ length: columns }, (_, ci) => parsedRows[ri]?.[ci] ?? '')
                                    );
                                  }

                                  // S'assurer que grid correspond bien à rows/columns
                                  if (grid.length !== rows) {
                                    grid = Array.from({ length: rows }, (_, ri) => grid[ri] || []);
                                  }
                                  grid = grid.map((row) => {
                                    if (!Array.isArray(row)) return Array.from({ length: columns }, () => '');
                                    if (row.length < columns) {
                                      return [...row, ...Array.from({ length: columns - row.length }, () => '')];
                                    }
                                    return row.slice(0, columns);
                                  });

                                  const content = grid.map((r) => r.join(' | ')).join('\n');
                                  return { ...clone, rows, columns, grid, content };
                                };

                                const update = (updater) => {
                                  const normalized = tbls.map((t) => ensureShape(t));
                                  const next = updater(normalized).map((t) => {
                                    const withShape = ensureShape(t);
                                    return {
                                      ...withShape,
                                      content: withShape.grid.map((r) => r.join(' | ')).join('\n'),
                                    };
                                  });
                                  onChangeEditField('sections', section.id, 'tablesText', JSON.stringify(next, null, 2));
                                };

                                return (
                                  <div className="mt-2 space-y-3">
                                    {tbls.map((t, idx) => {
                                      const shaped = ensureShape(t);
                                      return (
                                        <div key={idx} className="border rounded p-2 bg-white">
                                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                                            <div className="md:col-span-1">
                                              <label className="text-xs text-gray-600">Intitulé du tableau</label>
                                              <input
                                                className="w-full border border-gray-300 rounded p-1 text-sm"
                                                value={shaped.title || ''}
                                                onChange={(e) =>
                                                  update((arr) => {
                                                    arr[idx] = { ...shaped, title: e.target.value };
                                                    return arr;
                                                  })
                                                }
                                              />
                                            </div>
                                            <div>
                                              <label className="text-xs text-gray-600">Colonnes</label>
                                              <input
                                                type="number"
                                                min={1}
                                                max={12}
                                                className="w-full border border-gray-300 rounded p-1 text-sm"
                                                value={shaped.columns || 1}
                                                onChange={(e) => {
                                                  const nextCols = Math.max(1, Math.min(12, parseInt(e.target.value || '1', 10)));
                                                  update((arr) => {
                                                    const current = ensureShape(arr[idx] || shaped);
                                                    const newGrid = current.grid.map((row) => {
                                                      if (row.length < nextCols) {
                                                        return [...row, ...Array.from({ length: nextCols - row.length }, () => '')];
                                                      }
                                                      return row.slice(0, nextCols);
                                                    });
                                                    arr[idx] = normalizeTableShape(
                                                      { ...current, grid: newGrid },
                                                      nextCols
                                                    );
                                                    return arr;
                                                  });
                                                }}
                                              />
                                            </div>
                                            <div>
                                              <label className="text-xs text-gray-600">Lignes</label>
                                              <input
                                                type="number"
                                                min={1}
                                                max={30}
                                                className="w-full border border-gray-300 rounded p-1 text-sm"
                                                value={shaped.rows || 1}
                                                onChange={(e) => {
                                                  const nextRows = Math.max(1, Math.min(30, parseInt(e.target.value || '1', 10)));
                                                  update((arr) => {
                                                    const current = ensureShape(arr[idx] || shaped);
                                                    let newGrid = current.grid;
                                                    if (newGrid.length < nextRows) {
                                                      newGrid = [
                                                        ...newGrid,
                                                        ...Array.from({ length: nextRows - newGrid.length }, () =>
                                                          Array.from({ length: current.columns || 1 }, () => '')
                                                        ),
                                                      ];
                                                    } else if (newGrid.length > nextRows) {
                                                      newGrid = newGrid.slice(0, nextRows);
                                                    }
                                                    arr[idx] = { ...current, rows: nextRows, grid: newGrid };
                                                    return arr;
                                                  });
                                                }}
                                              />
                                            </div>
                                          </div>

                                          {/* Grille éditable */}
                                          <TableEditor
                                            table={shaped}
                                            onValidityChange={(isValid) =>
                                              updateTableValidity(
                                                'sections',
                                                section.id,
                                                idx,
                                                isValid
                                              )
                                            }
                                            onChange={(nextTable) =>
                                              update((arr) => {
                                                arr[idx] = nextTable;
                                                return arr;
                                              })
                                            }
                                          />
                                          <div className="overflow-x-auto">
                                            <table className="min-w-full border border-gray-200 text-xs">
                                              <tbody>
                                                {shaped.grid.map((row, ri) => (
                                                  <tr key={ri} className={ri === 0 ? 'bg-gray-50' : ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                    {row.map((cell, ci) => (
                                                      <td key={ci} className="border border-gray-200 p-1 align-top">
                                                        <input
                                                          className="w-full border border-gray-200 rounded px-1 py-0.5 text-[11px] bg-white"
                                                          value={cell}
                                                          onChange={(e) =>
                                                            update((arr) => {
                                                              const current = ensureShape(arr[idx] || shaped);
                                                              const g = current.grid.map((r) => [...r]);
                                                              g[ri][ci] = e.target.value;
                                                              arr[idx] = { ...current, grid: g };
                                                              return arr;
                                                            })
                                                          }
                                                        />
                                                      </td>
                                                    ))}
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>

                                          <div className="mt-2 flex items-center gap-2">
                                            <button
                                              type="button"
                                              className="px-2 py-1 text-xs bg-red-600 text-white rounded"
                                              onClick={() => update((arr) => arr.filter((_, i) => i !== idx))}
                                            >
                                              Supprimer
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                    <button
                                      type="button"
                                      className="px-3 py-1 text-sm bg-gray-100 border rounded"
                                      onClick={() =>
                                        update((arr) => [
                                          ...arr,
                                          {
                                            title: '',
                                            rows: 2,
                                            columns: 2,
                                            grid: [
                                              ['', ''],
                                              ['', ''],
                                            ],
                                            content: '|',
                                          },
                                        ])
                                      }
                                    >
                                      Ajouter un tableau
                                    </button>
                                  </div>
                                );
                              })()}
                              {hasInvalidTableLayout(
                                'sections',
                                section.id,
                                parseJsonArray(
                                  editStates.sections[section.id]?.tablesText
                                )
                              ) && (
                                <p
                                  role="alert"
                                  className="mt-2 text-sm text-red-700"
                                >
                                  Corrigez les largeurs de colonnes avant
                                  d’enregistrer.
                                </p>
                              )}
                              <div className="mt-2 flex items-center gap-2">
                                <button
                                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm"
                                  disabled={hasInvalidTableLayout(
                                    'sections',
                                    section.id,
                                    parseJsonArray(
                                      editStates.sections[section.id]
                                        ?.tablesText
                                    )
                                  )}
                                  onClick={() => saveSection(chapter, section)}
                                >
                                  Enregistrer
                                </button>
                                <button className="px-3 py-1 bg-gray-200 text-gray-800 rounded text-sm" onClick={() => cancelEdit('sections', section.id)}>Annuler</button>
                              </div>
                            </div>
                          ) : (
                            (editStates.sections[section.id]?.contentOverride || section.content) && (
                              <div className="text-gray-700 leading-relaxed mb-4 space-y-3 select-text [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6">
                                {renderRichOrLegacyContent(
                                  editStates.sections[section.id]?.contentOverride || section.content,
                                  editStates.sections[section.id]?.imagesOverride ?? section.images,
                                  editStates.sections[section.id]?.tablesOverride ?? section.tables
                                )}
                              </div>
                            )
                          )}

                          {/* Images de la section */}
                          {renderImages(
                            editStates.sections[section.id]?.imagesOverride ?? section.images,
                            editStates.sections[section.id]?.image_display_config || section.image_display_config
                          )}

                          {/* Vidéos de la section */}
                          {renderVideos(
                            editStates.sections[section.id]?.videosOverride ?? section.videos,
                            editStates.sections[section.id]?.image_display_config || section.image_display_config
                          )}

                          {/* Tableaux de la section */}
                          {renderTables(
                            editStates.sections[section.id]?.tablesOverride ?? section.tables,
                            editStates.sections[section.id]?.contentOverride || section.content
                          )}

                          {/* Sous-sections de la section */}
                          <div
                            data-testid="section-subsections"
                            className="min-w-0 max-w-full space-y-4 pl-4 pr-2 sm:pr-4"
                          >
                            {section.subsections.map((subsection, subsectionIndex) => (
                              <div 
                                key={subsection.id} 
                                data-hierarchy-item={`subsection:${subsection.id}`}
                                className={`w-full ${dragDropMode ? 'border-2 border-dashed border-yellow-300 rounded-lg p-2 bg-yellow-50/30 hover:bg-yellow-50/50 transition-colors my-2' : ''}`}
                                draggable={dragDropMode}
                                onDragStart={(e) => handleDragStart(e, 'subsection', subsection.id, chapter.id, section.id)}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDropSubsection(e, chapter.id, section.id, subsection.id)}
                              >
                                {/* Titre de la sous-section (H3) */}
                                <div className="mb-3">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-2 flex-1">
                                      {dragDropMode && (
                                        <GripVertical 
                                          size={18} 
                                          className="text-yellow-600 cursor-move flex-shrink-0" 
                                          title="Glisser pour déplacer"
                                        />
                                      )}
                                      <h3 
                                      id={`subsection-${subsection.id}`}
                                      className={`text-xl font-medium text-gray-700 ${selectedItem?.type === 'subsection' && (selectedItem?.subsectionId === subsection.id || (!selectedItem?.subsectionId && selectedItem?.chapterIndex === chapterIndexOriginal && selectedItem?.sectionIndex === sectionIndex && selectedItem?.subsectionIndex === subsectionIndex)) ? 'bg-yellow-50 border-l-4 border-yellow-500 pl-4 py-2 -ml-4' : ''}`}
                                      >
                                        {isIntro
                                          ? `${section.order + 1}.${subsection.order + 1} ${cleanTitle(subsection.title, subsection.order)}`
                                          : `${displayChapterNumber}.${section.order + 1}.${subsection.order + 1} ${cleanTitle(subsection.title, subsection.order)}`}
                                      </h3>
                                    </div>
                                    {/* Desktop: Boutons à côté du titre */}
                                    {canEditStructure && (
                                      <div className="hidden sm:flex items-center gap-2">
                                        <button
                                          type="button"
                                          onClick={() => retranslateScope('subsection', { subsection_id: subsection.id })}
                                          className="inline-flex items-center gap-1.5 mt-1 border border-green-300 text-green-800 bg-green-50 rounded-md px-3 py-1 text-sm hover:bg-green-100"
                                          title="Relancer la traduction de cette sous-section"
                                        >
                                          <Languages size={14} />
                                          Retraduire
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => startEdit('subsections', { id: subsection.id }, { content: subsection.content, images: subsection.images, videos: subsection.videos, tables: subsection.tables, title: subsection.title, image_display_config: subsection.image_display_config, video_display_config: subsection.video_display_config })}
                                          className="inline-flex items-center gap-1.5 mt-1 border border-gray-300 text-gray-800 rounded-md px-3 py-1 text-sm hover:bg-gray-50"
                                        >
                                          <Edit size={14} />
                                          Modifier
                                        </button>
                                        <button
                                          type="button"
                                          aria-label={`Monter ${isIntro ? `${section.order + 1}.${subsection.order + 1}` : `${displayChapterNumber}.${section.order + 1}.${subsection.order + 1}`}`}
                                          disabled={subsectionIndex === 0}
                                          onClick={() => moveRootSubsection(chapter.id, section.id, subsection.id, subsectionIndex - 1)}
                                          className="inline-flex items-center gap-1.5 mt-1 border border-gray-300 text-gray-800 rounded-md px-3 py-1 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                          <ChevronUp size={14} />
                                          Monter
                                        </button>
                                        <button
                                          type="button"
                                          aria-label={`Descendre ${isIntro ? `${section.order + 1}.${subsection.order + 1}` : `${displayChapterNumber}.${section.order + 1}.${subsection.order + 1}`}`}
                                          disabled={subsectionIndex === section.subsections.length - 1}
                                          onClick={() => moveRootSubsection(chapter.id, section.id, subsection.id, subsectionIndex + 1)}
                                          className="inline-flex items-center gap-1.5 mt-1 border border-gray-300 text-gray-800 rounded-md px-3 py-1 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                          <ChevronDown size={14} />
                                          Descendre
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => deleteSubsection(chapter.id, section.id, subsection.id)}
                                          className="inline-flex items-center gap-1.5 mt-1 border border-red-300 text-red-700 bg-red-50 rounded-md px-3 py-1 text-sm hover:bg-red-100"
                                          title="Supprimer cette sous-section"
                                        >
                                          <Trash2 size={14} />
                                          Supprimer
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  {/* Mobile: Boutons sur nouvelle ligne avec icônes seules */}
                                  {canEditStructure && (
                                    <div className="sm:hidden flex items-center gap-2 mt-2 justify-end">
                                      <button
                                        type="button"
                                        onClick={() => startEdit('subsections', { id: subsection.id }, { content: subsection.content, images: subsection.images, videos: subsection.videos, tables: subsection.tables, title: subsection.title, image_display_config: subsection.image_display_config, video_display_config: subsection.video_display_config })}
                                        className="inline-flex items-center justify-center border border-gray-300 text-gray-800 rounded-md p-1.5 text-sm hover:bg-gray-50"
                                        title="Modifier"
                                      >
                                        <Edit size={14} />
                                      </button>
                                      <button
                                        type="button"
                                        aria-label={`Monter ${isIntro ? `${section.order + 1}.${subsection.order + 1}` : `${displayChapterNumber}.${section.order + 1}.${subsection.order + 1}`}`}
                                        disabled={subsectionIndex === 0}
                                        onClick={() => moveRootSubsection(chapter.id, section.id, subsection.id, subsectionIndex - 1)}
                                        className="inline-flex items-center justify-center border border-gray-300 text-gray-800 rounded-md p-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                        title="Monter"
                                      >
                                        <ChevronUp size={14} />
                                      </button>
                                      <button
                                        type="button"
                                        aria-label={`Descendre ${isIntro ? `${section.order + 1}.${subsection.order + 1}` : `${displayChapterNumber}.${section.order + 1}.${subsection.order + 1}`}`}
                                        disabled={subsectionIndex === section.subsections.length - 1}
                                        onClick={() => moveRootSubsection(chapter.id, section.id, subsection.id, subsectionIndex + 1)}
                                        className="inline-flex items-center justify-center border border-gray-300 text-gray-800 rounded-md p-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                        title="Descendre"
                                      >
                                        <ChevronDown size={14} />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => deleteSubsection(chapter.id, section.id, subsection.id)}
                                        className="inline-flex items-center justify-center border border-red-300 text-red-700 bg-red-50 rounded-md p-1.5 text-sm hover:bg-red-100"
                                        title="Supprimer"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  )}
                                </div>

                                {renderContextualCreation(
                                  {
                                    currentType: 'subsection',
                                    currentItem: subsection,
                                    chapter,
                                    section,
                                    subsectionAncestors: [],
                                  },
                                  isIntro
                                    ? `${section.order + 1}.${subsection.order + 1}`
                                    : `${displayChapterNumber}.${section.order + 1}.${subsection.order + 1}`
                                )}

                                {/* Contenu de la sous-section */}
                                {editStates.subsections[subsection.id]?.editing ? (
                                  <div className="mb-3 border border-gray-200 rounded p-3 bg-gray-50">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Titre de la sous-section</label>
                                    <input
                                      type="text"
                                      className="w-full border border-gray-300 rounded p-2 text-sm mb-2"
                                      value={
                                        typeof editStates.subsections[subsection.id]?.title === 'string'
                                          ? editStates.subsections[subsection.id].title
                                          : (subsection.title || '')
                                      }
                                      onChange={(e) => onChangeEditField('subsections', subsection.id, 'title', e.target.value)}
                                    />
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Contenu (texte)</label>
                                    <RichTextEditor
                                      content={editStates.subsections[subsection.id]?.content || ''}
                                      onChange={(value) => onChangeEditField('subsections', subsection.id, 'content', value)}
                                      images={parseJsonArray(editStates.subsections[subsection.id]?.imagesText)}
                                      tables={parseJsonArray(editStates.subsections[subsection.id]?.tablesText)}
                                      onImagesChange={(images) =>
                                        onChangeEditField(
                                          'subsections',
                                          subsection.id,
                                          'imagesText',
                                          JSON.stringify(images, null, 2)
                                        )
                                      }
                                      onTablesChange={(tables) =>
                                        onChangeEditField(
                                          'subsections',
                                          subsection.id,
                                          'tablesText',
                                          JSON.stringify(tables, null, 2)
                                        )
                                      }
                                      onUploadImage={uploadInlineImage}
                                      contentLanguage={contentLanguage}
                                      canManageImages={canEditStructure}
                                      canManageImageStructure={canManageImageStructure}
                                      placeholder="Écrivez le contenu de votre sous-section ici..."
                                      editable={true}
                                      className="mb-2"
                                    />
                                    <div className="mb-3 space-y-3">
                                      <h3 className="text-sm font-semibold text-gray-800">
                                        Propositions des images de galerie
                                      </h3>
                                      <GalleryImageProposalsEditor
                                        images={parseJsonArray(
                                          editStates.subsections[subsection.id]
                                            ?.imagesText
                                        )}
                                        onChange={(images) =>
                                          updateDraftImages(
                                            'subsections',
                                            subsection.id,
                                            images
                                          )
                                        }
                                        onUploadImage={uploadInlineImage}
                                        canEdit={canEditStructure}
                                        canManageStructure={canManageImageStructure}
                                      />
                                    </div>
                                    {/* Configuration d'affichage des images */}
                                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                      <div>
                                        <label className="block text-xs text-gray-600 mb-1">Taille des images</label>
                                        <select
                                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                          value={editStates.subsections[subsection.id]?.image_display_config?.size || subsection.image_display_config?.size || 'medium'}
                                          onChange={(e) => {
                                            const currentConfig = editStates.subsections[subsection.id]?.image_display_config || subsection.image_display_config || {};
                                            onChangeEditField('subsections', subsection.id, 'image_display_config', { ...currentConfig, size: e.target.value });
                                          }}
                                        >
                                          <option value="small">Petite</option>
                                          <option value="medium">Moyenne</option>
                                          <option value="large">Grande</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label className="block text-xs text-gray-600 mb-1">Nombre de colonnes</label>
                                        <select
                                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                          value={editStates.subsections[subsection.id]?.image_display_config?.columns || subsection.image_display_config?.columns || 2}
                                          onChange={(e) => {
                                            const currentConfig = editStates.subsections[subsection.id]?.image_display_config || subsection.image_display_config || {};
                                            onChangeEditField('subsections', subsection.id, 'image_display_config', { ...currentConfig, columns: parseInt(e.target.value) });
                                          }}
                                        >
                                          <option value="1">1 par ligne</option>
                                          <option value="2">2 par ligne</option>
                                          <option value="3">3 par ligne</option>
                                        </select>
                                      </div>
                                    </div>

                                    {/* Vidéos: upload */}
                                    <div className="mt-3 flex flex-col gap-1 mr-4">
                                      <label className="text-xs font-medium text-gray-700">Vidéos</label>
                                      <input
                                        type="file"
                                        accept="video/*"
                                        onChange={async (e) => {
                                          const file = e.target.files?.[0];
                                          if (!file) return;
                                          const form = new FormData();
                                          form.append('file', file);
                                          try {
                                            const res = await api.post('/uploads/videos/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
                                            const payload = res?.data;
                                            const arr = (() => { try { return JSON.parse(editStates.subsections[subsection.id]?.videosText || '[]'); } catch { return []; } })();
                                            arr.push(payload);
                                            onChangeEditField('subsections', subsection.id, 'videosText', JSON.stringify(arr, null, 2));
                                          } catch (err) {
                                            console.error('Upload vidéo sous-section échoué', err);
                                          } finally {
                                            e.target.value = '';
                                          }
                                        }}
                                      />
                                      <span className="text-xs text-gray-500">Uploader et ajouter automatiquement à la liste</span>
                                    </div>

                                    {/* Configuration d'affichage des vidéos */}
                                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                      <div>
                                        <label className="block text-xs text-gray-600 mb-1">Taille des vidéos</label>
                                        <select
                                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                          value={editStates.subsections[subsection.id]?.video_display_config?.size || subsection.video_display_config?.size || 'medium'}
                                          onChange={(e) => {
                                            const currentConfig = editStates.subsections[subsection.id]?.video_display_config || subsection.video_display_config || {};
                                            onChangeEditField('subsections', subsection.id, 'video_display_config', { ...currentConfig, size: e.target.value });
                                          }}
                                        >
                                          <option value="small">Petite</option>
                                          <option value="medium">Moyenne</option>
                                          <option value="large">Grande</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label className="block text-xs text-gray-600 mb-1">Nombre de colonnes</label>
                                        <select
                                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                          value={editStates.subsections[subsection.id]?.video_display_config?.columns || subsection.video_display_config?.columns || 2}
                                          onChange={(e) => {
                                            const currentConfig = editStates.subsections[subsection.id]?.video_display_config || subsection.video_display_config || {};
                                            onChangeEditField('subsections', subsection.id, 'video_display_config', { ...currentConfig, columns: parseInt(e.target.value) });
                                          }}
                                        >
                                          <option value="1">1 par ligne</option>
                                          <option value="2">2 par ligne</option>
                                          <option value="3">3 par ligne</option>
                                        </select>
                                      </div>
                                    </div>

                                    {(() => {
                                      let arr = [];
                                      try { arr = JSON.parse(editStates.subsections[subsection.id]?.videosText || '[]'); } catch {}
                                      if (!Array.isArray(arr) || arr.length === 0) return null;
                                      return (
                                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                                          {arr.map((vid, idx) => {
                                            const url = typeof vid === 'string' ? vid : vid?.url;
                                            const caption = typeof vid === 'object' ? vid?.caption : undefined;
                                            const fullUrl = buildImageUrl(url || '');
                                            return (
                                              <div key={idx} className="border rounded p-2 flex flex-col gap-2 bg-white">
                                                {fullUrl ? (
                                                  <video src={fullUrl} controls className="w-full h-40 bg-black rounded" />
                                                ) : (
                                                  <div className="text-xs text-gray-500">Entrée de vidéo invalide</div>
                                                )}
                                                <input
                                                  type="text"
                                                  className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                                                  placeholder="Titre / légende de la vidéo"
                                                  value={caption || ''}
                                                  onChange={(e) => {
                                                    const next = arr.map((item, i) => {
                                                      if (i !== idx) return item;
                                                      if (typeof item === 'string') {
                                                        return { url: item, caption: e.target.value };
                                                      }
                                                      return { ...item, caption: e.target.value };
                                                    });
                                                    onChangeEditField('subsections', subsection.id, 'videosText', JSON.stringify(next, null, 2));
                                                  }}
                                                />
                                                <button
                                                  type="button"
                                                  className="self-start px-2 py-1 text-xs bg-red-600 text-white rounded"
                                                  onClick={() => {
                                                    const next = arr.filter((_, i) => i !== idx);
                                                    onChangeEditField('subsections', subsection.id, 'videosText', JSON.stringify(next, null, 2));
                                                  }}
                                                >Supprimer</button>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      );
                                    })()}
                                    {/* Tableaux: UI structurée (JSON caché) */}
                                    {(() => {
                                      let tbls = [];
                                      try { tbls = JSON.parse(editStates.subsections[subsection.id]?.tablesText || '[]'); } catch {}
                                      if (!Array.isArray(tbls)) tbls = [];

                                      const ensureShape = (t) => {
                                        const clone = { ...(t || {}) };
                                        let rows = Number.isInteger(clone.rows) ? clone.rows : null;
                                        let columns = Number.isInteger(clone.columns) ? clone.columns : null;
                                        let grid = Array.isArray(clone.grid) ? clone.grid.map((r) => Array.isArray(r) ? [...r] : []) : null;

                                        if (!rows || !columns || !grid || grid.length === 0) {
                                          const c = clone.content;
                                          let rawLines = [];
                                          if (typeof c === 'string') {
                                            rawLines = c.split('\n');
                                          } else if (Array.isArray(c)) {
                                            rawLines = c.flatMap((item) => (typeof item === 'string' ? item.split('\n') : []));
                                          }
                                          let lines = rawLines.map((l) => (l ?? '').trim()).filter((l) => l.length > 0);
                                          const hasPipe = lines.some((l) => l.includes('|'));
                                          if (hasPipe) {
                                            lines = lines.filter((l) => l.includes('|'));
                                          }
                                          const parsedRows = lines.map((l) => l.split('|').map((cell) => cell.trim()));
                                          const colCount = parsedRows.length > 0 ? Math.max(...parsedRows.map((r) => r.length)) : 2;
                                          const rowCount = parsedRows.length > 0 ? parsedRows.length : 2;
                                          rows = rowCount;
                                          columns = colCount;
                                          grid = Array.from({ length: rows }, (_, ri) =>
                                            Array.from({ length: columns }, (_, ci) => parsedRows[ri]?.[ci] ?? '')
                                          );
                                        }

                                        if (grid.length !== rows) {
                                          grid = Array.from({ length: rows }, (_, ri) => grid[ri] || []);
                                        }
                                        grid = grid.map((row) => {
                                          if (!Array.isArray(row)) return Array.from({ length: columns }, () => '');
                                          if (row.length < columns) {
                                            return [...row, ...Array.from({ length: columns - row.length }, () => '')];
                                          }
                                          return row.slice(0, columns);
                                        });

                                        const content = grid.map((r) => r.join(' | ')).join('\n');
                                        return { ...clone, rows, columns, grid, content };
                                      };

                                      const update = (updater) => {
                                        const normalized = tbls.map((t) => ensureShape(t));
                                        const next = updater(normalized).map((t) => {
                                          const withShape = ensureShape(t);
                                          return {
                                            ...withShape,
                                            content: withShape.grid.map((r) => r.join(' | ')).join('\n'),
                                          };
                                        });
                                        onChangeEditField('subsections', subsection.id, 'tablesText', JSON.stringify(next, null, 2));
                                      };

                                      return (
                                        <div className="mt-2 space-y-3">
                                          {tbls.map((t, idx) => {
                                            const shaped = ensureShape(t);
                                            return (
                                              <div key={idx} className="border rounded p-2 bg-white">
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                                                  <div className="md:col-span-1">
                                                    <label className="text-xs text-gray-600">Intitulé du tableau</label>
                                                    <input
                                                      className="w-full border border-gray-300 rounded p-1 text-sm"
                                                      value={shaped.title || ''}
                                                      onChange={(e) =>
                                                        update((arr) => {
                                                          arr[idx] = { ...shaped, title: e.target.value };
                                                          return arr;
                                                        })
                                                      }
                                                    />
                                                  </div>
                                                  <div>
                                                    <label className="text-xs text-gray-600">Colonnes</label>
                                                    <input
                                                      type="number"
                                                      min={1}
                                                      max={12}
                                                      className="w-full border border-gray-300 rounded p-1 text-sm"
                                                      value={shaped.columns || 1}
                                                      onChange={(e) => {
                                                        const nextCols = Math.max(1, Math.min(12, parseInt(e.target.value || '1', 10)));
                                                        update((arr) => {
                                                          const current = ensureShape(arr[idx] || shaped);
                                                          const newGrid = current.grid.map((row) => {
                                                            if (row.length < nextCols) {
                                                              return [...row, ...Array.from({ length: nextCols - row.length }, () => '')];
                                                            }
                                                            return row.slice(0, nextCols);
                                                          });
                                                          arr[idx] = normalizeTableShape(
                                                            { ...current, grid: newGrid },
                                                            nextCols
                                                          );
                                                          return arr;
                                                        });
                                                      }}
                                                    />
                                                  </div>
                                                  <div>
                                                    <label className="text-xs text-gray-600">Lignes</label>
                                                    <input
                                                      type="number"
                                                      min={1}
                                                      max={30}
                                                      className="w-full border border-gray-300 rounded p-1 text-sm"
                                                      value={shaped.rows || 1}
                                                      onChange={(e) => {
                                                        const nextRows = Math.max(1, Math.min(30, parseInt(e.target.value || '1', 10)));
                                                        update((arr) => {
                                                          const current = ensureShape(arr[idx] || shaped);
                                                          let newGrid = current.grid;
                                                          if (newGrid.length < nextRows) {
                                                            newGrid = [
                                                              ...newGrid,
                                                              ...Array.from({ length: nextRows - newGrid.length }, () =>
                                                                Array.from({ length: current.columns || 1 }, () => '')
                                                              ),
                                                            ];
                                                          } else if (newGrid.length > nextRows) {
                                                            newGrid = newGrid.slice(0, nextRows);
                                                          }
                                                          arr[idx] = { ...current, rows: nextRows, grid: newGrid };
                                                          return arr;
                                                        });
                                                      }}
                                                    />
                                                  </div>
                                                </div>

                                                <div className="overflow-x-auto">
                                                <TableEditor
                                                  table={shaped}
                                                  onValidityChange={(isValid) =>
                                                    updateTableValidity(
                                                      'subsections',
                                                      subsection.id,
                                                      idx,
                                                      isValid
                                                    )
                                                  }
                                                  onChange={(nextTable) =>
                                                      update((arr) => {
                                                        arr[idx] = nextTable;
                                                        return arr;
                                                      })
                                                    }
                                                  />
                                                  <table className="min-w-full border border-gray-200 text-xs">
                                                    <tbody>
                                                      {shaped.grid.map((row, ri) => (
                                                        <tr key={ri} className={ri === 0 ? 'bg-gray-50' : ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                          {row.map((cell, ci) => (
                                                            <td key={ci} className="border border-gray-200 p-1 align-top">
                                                              <input
                                                                className="w-full border border-gray-200 rounded px-1 py-0.5 text-[11px] bg-white"
                                                                value={cell}
                                                                onChange={(e) =>
                                                                  update((arr) => {
                                                                    const current = ensureShape(arr[idx] || shaped);
                                                                    const g = current.grid.map((r) => [...r]);
                                                                    g[ri][ci] = e.target.value;
                                                                    arr[idx] = { ...current, grid: g };
                                                                    return arr;
                                                                  })
                                                                }
                                                              />
                                                            </td>
                                                          ))}
                                                        </tr>
                                                      ))}
                                                    </tbody>
                                                  </table>
                                                </div>

                                                <div className="mt-2 flex items-center gap-2">
                                                  <button
                                                    type="button"
                                                    className="px-2 py-1 text-xs bg-red-600 text-white rounded"
                                                    onClick={() => update((arr) => arr.filter((_, i) => i !== idx))}
                                                  >
                                                    Supprimer
                                                  </button>
                                                </div>
                                              </div>
                                            );
                                          })}
                                          <button
                                            type="button"
                                            className="px-3 py-1 text-sm bg-gray-100 border rounded"
                                            onClick={() =>
                                              update((arr) => [
                                                ...arr,
                                                {
                                                  title: '',
                                                  rows: 2,
                                                  columns: 2,
                                                  grid: [
                                                    ['', ''],
                                                    ['', ''],
                                                  ],
                                                  content: '|',
                                                },
                                              ])
                                            }
                                          >
                                            Ajouter un tableau
                                          </button>
                                        </div>
                                      );
                                    })()}
                                    {hasInvalidTableLayout(
                                      'subsections',
                                      subsection.id,
                                      parseJsonArray(
                                        editStates.subsections[subsection.id]
                                          ?.tablesText
                                      )
                                    ) && (
                                      <p
                                        role="alert"
                                        className="mt-2 text-sm text-red-700"
                                      >
                                        Corrigez les largeurs de colonnes avant
                                        d’enregistrer.
                                      </p>
                                    )}
                                    <div className="mt-2 flex items-center gap-2">
                                      <button
                                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm"
                                        disabled={hasInvalidTableLayout(
                                          'subsections',
                                          subsection.id,
                                          parseJsonArray(
                                            editStates.subsections[
                                              subsection.id
                                            ]?.tablesText
                                          )
                                        )}
                                        onClick={() =>
                                          saveSubsection(
                                            chapter,
                                            section,
                                            subsection
                                          )
                                        }
                                      >
                                        Enregistrer
                                      </button>
                                      <button className="px-3 py-1 bg-gray-200 text-gray-800 rounded text-sm" onClick={() => cancelEdit('subsections', subsection.id)}>Annuler</button>
                                    </div>
                                  </div>
                                ) : (
                                  (editStates.subsections[subsection.id]?.contentOverride || subsection.content) && (
                                    <div className="text-gray-600 leading-relaxed mb-3 space-y-2 select-text [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6">
                                      {renderRichOrLegacyContent(
                                        editStates.subsections[subsection.id]?.contentOverride || subsection.content,
                                        editStates.subsections[subsection.id]?.imagesOverride ?? subsection.images,
                                        editStates.subsections[subsection.id]?.tablesOverride ?? subsection.tables
                                      )}
                                    </div>
                                  )
                                )}

                                {/* Images de la sous-section */}
                                {renderImages(
                                  editStates.subsections[subsection.id]?.imagesOverride ?? subsection.images,
                                  editStates.subsections[subsection.id]?.image_display_config || subsection.image_display_config
                                )}

                                {/* Vidéos de la sous-section */}
                                {renderVideos(
                                  editStates.subsections[subsection.id]?.videosOverride ?? subsection.videos,
                                  editStates.subsections[subsection.id]?.video_display_config || subsection.video_display_config || editStates.subsections[subsection.id]?.image_display_config || subsection.image_display_config
                                )}

                                {/* Tableaux de la sous-section */}
                                {renderTables(
                                  editStates.subsections[subsection.id]?.tablesOverride ?? subsection.tables,
                                  editStates.subsections[subsection.id]?.contentOverride || subsection.content
                                )}

                                <RecursiveSubsectionChildren
                                  parentNode={subsection}
                                  numberingPrefix={
                                    isIntro
                                      ? `${section.order + 1}.${subsection.order + 1}`
                                      : `${displayChapterNumber}.${section.order + 1}.${subsection.order + 1}`
                                  }
                                  bookId={bookId}
                                  chapterId={chapter.id}
                                  sectionId={section.id}
                                  canEditStructure={canEditStructure}
                                  selectedItem={selectedItem}
                                  selectedLang={selectedLang}
                                  sourceLanguage={sourceLanguage}
                                  contentLanguage={contentLanguage}
                                  renderContent={renderRichOrLegacyContent}
                                  renderImages={renderImages}
                                  renderVideos={renderVideos}
                                  renderTables={renderTables}
                                  onUploadImage={uploadInlineImage}
                                  activeCreateTarget={activeCreateTarget}
                                  creationLocked={
                                    Boolean(activeCreateTarget) ||
                                    createStates.chapter.open
                                  }
                                  onOpenCreateTarget={openContextualCreation}
                                  onCancelCreateTarget={cancelContextualCreation}
                                  onSubmitCreateTarget={submitContextualCreation}
                                  onChanged={async (change = {}) => {
                                    await refreshAtAnchor(
                                      change.anchorIds || [
                                        `subsection-${subsection.id}`,
                                        `section-${section.id}`,
                                        `chapter-${chapter.id}`,
                                      ]
                                    );
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Sous-sections directes du chapitre (sans section parente) */}
                    {chapter.subsections && chapter.subsections.length > 0 && (
                      <div className="min-w-0 max-w-full space-y-4 pl-4 pr-2 sm:pr-4">
                        {chapter.subsections.map((subsection, subsectionIndex) => (
                          <div key={subsection.id} className="w-full">
                            {/* Titre de la sous-section (H3) */}
                            <h3 
                            id={`subsection-${subsection.id}`}
                            className="text-xl font-medium text-gray-700 mb-3"
                          >
                              {isIntro
                                ? `${subsection.order + 1} ${cleanTitle(subsection.title, subsection.order)}`
                                : `${displayChapterNumber}.${subsection.order + 1} ${cleanTitle(subsection.title, subsection.order)}`}
                          </h3>

                            {/* Contenu de la sous-section */}
                            {(subsection.content) && (
                              <div className="text-gray-800 leading-relaxed mb-4 space-y-4 select-text [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6">
                                {renderRichOrLegacyContent(
                                  subsection.content,
                                  subsection.images,
                                  subsection.tables
                                )}
                              </div>
                            )}

                            {/* Images de la sous-section */}
                            {renderImages(subsection.images, subsection.image_display_config)}

                            {/* Vidéos de la sous-section */}
                            {renderVideos(subsection.videos, subsection.image_display_config)}

                            {/* Tableaux de la sous-section */}
                            {renderTables(subsection.tables, subsection.content)}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* QCMs du chapitre - Affichés à la fin */}
                    {chapter.qcm && chapter.qcm.length > 0 && (
                      <div className="w-full mt-8">
                        <div className="border-t-2 border-blue-200 pt-6">
                          <h2 className="text-2xl font-bold text-blue-900 mb-6 flex items-center gap-2">
                            {translateWithLanguage(contentLanguage, 'documentViewer.quizTitle')}
                          </h2>
                          <div className="space-y-6">
                            {chapter.qcm.map((qcm, qcmIndex) => (
                              <div key={qcm.id} id={`chapter-${chapter.id}-qcm-${qcmIndex}`} className="qcm-section">
                                <QCMComponent key={qcm.id} qcm={qcm} language={contentLanguage} />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FullBookContent;
